import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Data,
  NavigationEnd,
  Router,
  RouterEvent,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AdditiveCapabilities, AdditiveCapabilitiesAware } from '@core/access-control/additive-capabilities';
import { checkType, getIcon, getIconColor } from '@core/attachment/helpers';
import {
  AudioMediaTypes,
  ImageMediaTypes,
  MediaType,
  PdfMediaTypes,
  VideoMediaTypes,
} from '@core/attachment/media-type.typings';
import { AuthorizationService } from '@core/authorization/authorization.service';
import { DataroomQuery } from '@core/dataroom/dataroom.query';
import { DataroomService } from '@core/dataroom/dataroom.service';
import { IssuingSectionStatusesDTO } from '@core/dto/issuing-section-statuses.dto';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { Organization, OrganizationType } from '@core/models/organization.model';
import { Partner } from '@core/models/partner.model';
import { OrganizationQuery } from '@core/organization/organization.query';
import { OrganizationContextService } from '@core/context/organization-context.service';
import { filterNil } from '@datorama/akita';
import { WaiValidators } from '@feature/forms/wai-validators';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Breadcrumb, BreadcrumbsService } from '@shared/breadcrumbs/breadcrumbs.typings';
import { ConfigurableViewRouteData } from '@shared/route-data';
import { dataroomRoute } from '@shared/shared-routes';
import { combineLatest, of, BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';

import { DataroomActionsService } from './dataroom-actions.service';
import { DataroomBreadcrumbsService } from './dataroom-breadcrumbs.service';
import { defaultDataSourceOptions, DataroomDataSource } from './dataroom-data-source';
import { mainParentId } from './dataroom-defaults';
import { UserDataroomService } from '@core/dataroom/user-dataroom.service';
import { ClientDataroomService } from '@core/dataroom/client-dataroom.service';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';
import { AppStoreQuery } from '@core/state';
import { ResponsiveService } from '@core/responsive/responsive.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NodeInfoDialogComponent } from './node-info-dialog/node-info-dialog.component';
import { FilenameTruncateService } from './filename-truncate.service';
import { DueDiligenceService } from '@core/product/due-diligence.service';
import { ReadOnlyUserService } from '@core/read-only-user/read-only-user.service';
import { CreateFundDialogPrefillService } from '@feature/fund-ddq-runs/create-fund-dialog/create-fund-dialog-prefill.service';
import { timer } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { catchError, finalize } from 'rxjs/operators';
import { CreateFundDialogComponent } from '@feature/fund-ddq-runs/create-fund-dialog/create-fund-dialog.component';
import { TemplateDDService } from '@core/template-dd/template-dd.service';
import {
  extractCreatedForUserIdFromRoute,
  isTemplateClientContext,
} from '@core/template-dd/template-client-context.util';
import {
  clearStoredTemplateClientContextClientId,
  getStoredTemplateClientContextClientId,
  setStoredTemplateClientContextClientId,
} from '@core/template-dd/template-client-context.storage';
import { ClientQuery } from '@core/client/client.query';
import { Client } from '@core/models/client.model';
import { resolveProcessQueueOrgName } from '@core/template-dd/process-queue-org-name.util';
import { buildProcessQueueOrgNameBaseContext } from '@core/template-dd/process-queue-org-name-context.util';
import { FundNameDialogComponent, FundNameDialogResult } from './fund-name-dialog/fund-name-dialog.component';
import { ProductFormGroup, ProductFormService } from '@feature/product/product-form.service';
import { SubscriptionAccessService } from '@core/subscription/subscription-access.service';
import { SubscriptionService } from '@core/subscription/subscription.service';
import {
  coerceDdTemplateTypeForTierLock,
  isDdCustomTemplateTypeDisabled,
} from '@core/template-dd/dd-template-type-tier.util';
import { SubscriptionUpgradeDialogComponent } from '@shared/subscription/subscription-upgrade-dialog/subscription-upgrade-dialog.component';
import { DialogComponent, DialogData, DialogResult } from '@shared/dialog/dialog.component';
import { ProductService } from '@core/product/product.service';

interface FundNameOption {
  id: string;
  name: string;
  source: 'user' | 'ai';
  isEditing: boolean;
}

interface DefaultTemplateSelection {
  id: number | string;
  name: string;
  kind: DdTemplateKind;
}

interface DefaultTemplateAssetMatchingDraft {
  assetClassId: string;
  assetClass: string;
  subAssetClassId: string;
  subAssetClass: string;
  type: DdTemplateKind;
  templateId: string;
  templateName: string;
  rowId: number | null;
  sourceScopeType: 'PARTNER' | 'EMITTENT' | 'CLIENT' | null;
}

interface DefaultTemplateAssetMatchingOption {
  label: string;
  value: string;
  mappingIds?: number[];
}

export interface NavigableNode {
  id: string;
  name: string;
  ancestors?: {
    id: string;
    name: string;
  };
}

type NavigationEvent = RouterEvent | RouterStateSnapshot;

export type DataroomComponentRouteData = AdditiveCapabilitiesAware & ConfigurableViewRouteData;

type DdTemplateKind = 'standard' | 'custom';
type DataroomTemplateTab = 'DDQ' | 'IM';

interface DdTemplateOption {
  id: string;
  name: string;
  kind: DdTemplateKind;
  raw: any;
}

@UntilDestroy()
@Component({
  selector: 'wai-dataroom',
  templateUrl: './dataroom.component.html',
  styleUrls: ['./dataroom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: BreadcrumbsService, useClass: DataroomBreadcrumbsService }],
  standalone: false,
})
export class DataroomComponent implements AfterViewInit, AdditiveCapabilitiesAware, OnInit {
  private readonly clientQuery = inject(ClientQuery, { optional: true });
  private _service: DataroomService | UserDataroomService | ClientDataroomService = this.productService;

  @Input() set service(value: DataroomService | UserDataroomService | ClientDataroomService) {
    this._service = value;
  }

  get service(): DataroomService | UserDataroomService | ClientDataroomService {
    return this._service;
  }
  @Output() keyMetricsStatusChanged = new EventEmitter<boolean>();

  public displayedColumns = ['select', 'name', 'modifiedAt', 'file.size', 'category', 'menu'];
  public dataSource: DataroomDataSource;
  /** Always a valid stream for the table loader (dataSource is created in ngOnInit). */
  public tableLoading$: Observable<boolean> = of(true);
  /** KAP-1887: non-null once a getNodes() call comes back blocked by dataroom_open. */
  public accessDeniedReason$: Observable<string | null> = of(null);
  public search: FormControl = new FormControl('', [WaiValidators.nonEmpty]);
  public selection = new SelectionModel<DataroomNode>(true, []);
  @ViewChild(MatSort) public sort: MatSort;
  public loading: Observable<boolean> = this.query.selectLoading();
  public selectedScope: BehaviorSubject<NavigableNode> = new BehaviorSubject(this.emptyNode);
  @Input() public capabilities: AdditiveCapabilities = (this.activatedRoute.snapshot.data as AdditiveCapabilitiesAware)
    .capabilities;
  private activeScope: BehaviorSubject<NavigableNode> = new BehaviorSubject(this.emptyNode);
  private navigation: Observable<NavigationEvent>;
  private accessGrant: BehaviorSubject<IssuingSectionStatusesDTO> = new BehaviorSubject(
    {} as IssuingSectionStatusesDTO,
  );
  isProductDataroom = false;
  canShowDueDiligence = false;
  /** Controls visibility of the invite ("person_add") button based on partner DD flag. */
  public isDueDiligenceEnabled$: Observable<boolean>;
  public isClient = false;
  user = this.appStoreQuery.selectUser();
  recommended_category_list: any;
  disableUpload: boolean = false;
  failedMesssage: string | null = null;
  blinkerMessage: string | null = 'Documents are being analyzed.';
  showFailedMessage: boolean = false;
  showBlinkerMessage: boolean = false;
  //private documentUploadFailureMessage: string | null = null;
  //private jobFailureMessage: string | null = null;
  public documentList: any;
  private readOnlyUserService = inject(ReadOnlyUserService);
  public vectorStoreJobStatus: boolean = false;
  public documentCategoryJobStatus: boolean = false;
  public isScoringInProgress = false;
  public isDefaultTemplateScoreRecalculationInProgress = false;

  public get isReadOnly(): boolean {
    return this.readOnlyUserService.getIsReadOnlyUser();
  }

  public get canEditToolbarActionsInFundDdqContext(): boolean {
    return this.organizationContextService.isInFundDDQRunsContextSync() && this.isEmissionLocked;
  }

  public get shouldDisableToolbarActions(): boolean {
    return this.isReadOnly && !this.canEditToolbarActionsInFundDdqContext;
  }

  /**
   * Subscription-driven restriction:
   * LP + lp_free => disable DD launch and show "Read Only access" tooltip
   */
  public isLpFreeAccess = false;
  /** True when the signed-in profile is an advisor — DD launch is read-only for advisors. */
  public isAdvisorUser = false;
  /** LP free/standard — custom DD template type is locked (standard remains available). */
  public isLpCustomTemplateLocked = false;
  /** GP issuer without `gp_addon_due_diligence` — custom DD template type is locked. */
  public isGpCustomTemplateLocked = false;
  public isDdqRunsExhausted = false;
  dataroomFolder = {
    'Advise Folder': 'Advise Folder',
    'General Information Folder': this.translate.instant('INTL_GENERAL_INFORMATION_FOLDER'),
    'Financial Folder': this.translate.instant('INTL_FINANCIAL_FOLDER'),
    'Personnel Folder': this.translate.instant('INTL_PERSSONEL_FOLDER'),
    'Due Diligence / ESG Folder': this.translate.instant('INTL_DUE_DILIGENCE_FOLDER'),

    Other: this.translate.instant('INTL_OTHER'),
  };
  hasFile: Observable<boolean> = of(false);
  productuid: any;
  totalScores: any;
  totalScoreslevel: any;
  partneruid: any;
  vectorDbId: any = null;
  emittentuid: any = null;
  @ViewChild('dueDiligenceModal') dueDiligenceModal!: TemplateRef<any>;
  @ViewChild('fundNameModal') fundNameModal!: TemplateRef<any>;
  @ViewChild('defaultTemplateAssetMatchingModal') defaultTemplateAssetMatchingModal!: TemplateRef<any>;
  @ViewChild('imDefaultTemplateAssetMatchingModal') imDefaultTemplateAssetMatchingModal!: TemplateRef<any>;
  isHandset$: Observable<boolean>;
  /** True for handset and tablet - use simplified table columns (Select, Name, Actions) */
  isCompactView$: Observable<boolean>;
  private categorizationSubject = new BehaviorSubject<any>(null);
  categoryNamesForMissingDoc: string[] = [];
  categoryNamesForMandatoryDoc: string[] = [];
  documents: any;
  isDocDelete: any;
  userID: any;
  isAnalystUser = false;
  progressValue = 0;
  progressInterval: any;
  showProgressBar = false;
  isUploadingVectorStore = false;
  isDocumentCategorize = false;
  productForm: any;
  ddSignedOff = false; // Track if DD is signed off
  hasInProgressDdRun = false;
  hasInProgressImRun = false;
  isProcessing: boolean = false;

  templateTypeControl = new FormControl<DdTemplateKind | null>(null);
  templateNameControl = new FormControl<DdTemplateOption | null>(null);
  templateTypeOptions: { label: string; value: DdTemplateKind }[] = [
    { label: 'Standard', value: 'standard' },
    { label: 'Custom', value: 'custom' },
  ];
  templateNameOptions: DdTemplateOption[] = [];
  private allTemplateNameOptions: DdTemplateOption[] = [];
  isTemplateOptionsLoading = false;
  hasSelectedTemplateOnce = false;
  private hasLoadedTemplateOptions = false;
  private pendingDefaultTemplateSelection: DefaultTemplateSelection | null = null;
  private pendingImDefaultTemplateSelection: DefaultTemplateSelection | null = null;
  public defaultTemplateAssetMatchingDraft: DefaultTemplateAssetMatchingDraft | null = null;
  private savedDefaultTemplateAssetMatchingDraft: DefaultTemplateAssetMatchingDraft | null = null;
  public defaultTemplateAssetClassOptions: DefaultTemplateAssetMatchingOption[] = [];
  public defaultTemplateSubAssetClassOptions: DefaultTemplateAssetMatchingOption[] = [];
  public defaultTemplateNameOptions: DefaultTemplateAssetMatchingOption[] = [];
  public isDefaultTemplateAssetMatchingLoading = false;
  public showDefaultTemplateCreateHelperInModal = false;
  public hasResolvedDefaultTemplateOptionsInModal = false;
  public imDefaultTemplateAssetMatchingDraft: DefaultTemplateAssetMatchingDraft | null = null;
  private savedImDefaultTemplateAssetMatchingDraft: DefaultTemplateAssetMatchingDraft | null = null;
  public imDefaultTemplateAssetClassOptions: DefaultTemplateAssetMatchingOption[] = [];
  public imDefaultTemplateSubAssetClassOptions: DefaultTemplateAssetMatchingOption[] = [];
  public imDefaultTemplateNameOptions: DefaultTemplateAssetMatchingOption[] = [];
  public isImDefaultTemplateAssetMatchingLoading = false;
  public showImDefaultTemplateCreateHelperInModal = false;
  public hasResolvedImDefaultTemplateOptionsInModal = false;
  public activeTemplateTab: DataroomTemplateTab = 'DDQ';

  public isEmissionLocked = false;
  fundNameOptions: FundNameOption[] = [];
  selectedFundName = '';
  savedSelectedFundName = '';
  isFundNameSuggestionsLoading = false;
  private hasFundNameLookupAttempted = false;
  private emittentId: string | null = null;
  private fundNameDialogRef: MatDialogRef<FundNameDialogComponent, FundNameDialogResult | null> | null = null;
  private vectorStoreJobId: string | null = null;
  private documentCategoryJobId: string | null = null;
  private readonly scoringJobPollingMaxAttempts = 30;
  private documentTaggingTemplateScope: {
    scopeType: 'PARTNER' | 'EMITTENT' | 'CLIENT';
    scopeUid: string;
  } | null = null;
  public productForm1: Observable<ProductFormGroup> = this.formService.form;
  emittentCompanyName:any;

  constructor(
    private actionsService: DataroomActionsService,
    public activatedRoute: ActivatedRoute,
    private authorizationService: AuthorizationService,
    @Inject(BreadcrumbsService) private breadcrumbs: DataroomBreadcrumbsService,
    private organizationContextService: OrganizationContextService,
    private organizationQuery: OrganizationQuery,
    private query: DataroomQuery,
    public router: Router,
    private productService: DataroomService,
    private translate: TranslateService,
    private snackBarService: SnackBarService,
    private appStoreQuery: AppStoreQuery,
    private responsiveService: ResponsiveService,
    private dialog: MatDialog,
    private filenameTruncateService: FilenameTruncateService,
    private cr: ChangeDetectorRef,
    private dueDiligenceService: DueDiligenceService,
    private subscriptionAccessService: SubscriptionAccessService,
    private subscriptionService: SubscriptionService,
    private templateDDService: TemplateDDService,
    private createFundDialogPrefillService: CreateFundDialogPrefillService,
    private formService: ProductFormService,
    private origProductService: ProductService,
  ) {}

  public ngOnInit(): void {
    this.organizationContextService.primeFundDdqContextFromActivatedRoute(this.activatedRoute);
    this.syncStoredTemplateClientIdFromContext();
    this.ensureFundDdqContext();
    this.isHandset$ = this.responsiveService.isHandset$;
    this.isDueDiligenceEnabled$ = this.subscriptionAccessService.selectDueDiligencePartnerFeaturesEnabled();
    this.isCompactView$ = combineLatest([this.responsiveService.isHandset$, this.responsiveService.isTablet$]).pipe(
      map(([isHandset, isTablet]) => isHandset || isTablet),
      shareReplay(1),
    );

    // Resolve route/product context before DD column + template subscriptions (they depend on productuid).
    const rawKeys = localStorage.getItem('keys');
    let parsedKeys: any = null;
    if (rawKeys) {
      try {
        parsedKeys = JSON.parse(rawKeys);
      } catch (error) {
        console.error('Error parsing localStorage item "keys":', error);
      }
    }

    this.partneruid = parsedKeys?.partneruid ?? null;
    this.emittentuid = parsedKeys?.emittentid ?? null;
    this.productuid = this.resolveProductIdFromRouteTree();
    this.userID = this.getCurrentUserId();
    this.canShowDueDiligence = parsedKeys?.isDueDiligence === true;
    this.isAnalystUser = localStorage.getItem('isAnalystUser') === 'true';

    // Partner DD visibility from subscription access (org + theme), not only static localStorage keys.
    combineLatest([this.isDueDiligenceEnabled$, this.isCompactView$])
      .pipe(untilDestroyed(this))
      .subscribe(([ddEnabled, isCompact]) => {
        const wasShowingDueDiligence = this.canShowDueDiligence;
        this.canShowDueDiligence = ddEnabled === true;
        this.updateDisplayedColumns(isCompact);

        if (this.productuid) {
          if (!wasShowingDueDiligence) {
            this.hasLoadedTemplateOptions = false;
            this.loadDdSignOffStatus();
            this.loadImInProgressStatus();
            this.loadImDefaultTemplateSelection();
          }
          this.loadTemplateOptions();
        }

        this.cr.markForCheck();
      });

    this.organizationQuery
      .selectChosenOrganization()
      .pipe(
        distinctUntilChanged((a, b) => (a?.id ?? null) === (b?.id ?? null)),
        untilDestroyed(this),
      )
      .subscribe((organization) => {
        const keysPartnerUid = this.readPartnerUidFromKeys();
        const partner = this.resolvedDueDiligencePartnerUid(organization, keysPartnerUid);
        const nextPartnerUid = partner?.uid ?? keysPartnerUid ?? null;

        if (nextPartnerUid === this.partneruid) {
          return;
        }

        this.partneruid = nextPartnerUid;
        if (!this.productuid) {
          this.cr.markForCheck();
          return;
        }

        //this.refreshDueDiligencePartnerData();
        if (this.productuid) {
          this.hasLoadedTemplateOptions = false;
          this.loadTemplateOptions();
          this.loadImDefaultTemplateSelection();
        }
        this.cr.markForCheck();
      });

    this.productForm = JSON.parse(localStorage.getItem('information'));

    if (this.productuid) {
      this.origProductService.getWithSubresources(this.productuid).subscribe((product) => {
        this.origProductService.getStore().setActive(product.id);
        if (this.productuid) {
          this.hasLoadedTemplateOptions = false;
          this.loadTemplateOptions();
          this.loadImDefaultTemplateSelection();
        }
      });
    }

    if (!this.productForm) {

      this.productForm1.subscribe((productForm: any) => {
        if (!productForm) {
          return;
        }
        this.productForm = productForm.getRawValue();
        this.productForm.emittentCompany = [{}];
        this.productForm.emittentCompany[0].name = this.productForm.productName;
        localStorage.setItem('information', JSON.stringify(this.productForm));
        this.emittentCompanyName = this.productForm?.emittentCompany?.[0]?.name ?? '';
        this.cr.detectChanges();
      });
    }

    this.emittentCompanyName = this.productForm?.emittentCompany?.[0]?.name ?? '';
    this.emittentId = this.resolveEmittentIdFromRouteOrOrgContext();
    this.loadSavedFundNameFromStorage();
    this.loadSavedTemplateSelectionFromStorage();
    this.isEmissionLocked = this.isEmissionStartedOrAwaiting();
    // Subscribe to uploadVectorStore status
    this.productService.uploadVectorStore$.subscribe((isUploading) => {
      this.isUploadingVectorStore = isUploading;
      this.cr.detectChanges();
    });
    this.productService.documentCategorizationStore$.subscribe((isCategorize : boolean) => {
      this.isDocumentCategorize = isCategorize;
      this.cr.detectChanges();
    });

    this.actionsService.setService(this.service);
    this.dataSource = new DataroomDataSource(this.service);
    this.tableLoading$ = this.dataSource.loading;
    this.accessDeniedReason$ = this.dataSource.accessDeniedReason$;
    this.hasFile = this.dataSource.dataAsObservable.pipe(map((data) => data.some((node) => node.type === 'file')));
    this.navigation = (this.router.events as Observable<NavigationEvent>).pipe(
      untilDestroyed(this),
      filter((event) => event instanceof NavigationEnd),
      startWith(this.router.routerState.snapshot),
    );
    this.shouldDisplayNoAccessPanel()
      .pipe(
        filter((should) => !should),
        take(1),
        untilDestroyed(this),
        tap(() => {
          this.service.setLoading(true);
          this.bindNavigationChange();
          this.bindPathUpdates();
        }),
      )
      .subscribe();

    this.categorizationSubject
      .pipe(
        filter((res) => !!res),
        untilDestroyed(this),
      )
      .subscribe((res) => {
        this.updateDocumentCategories(res);
      });

    if (this.productuid) {
      // this.insertTemplateProductMapping();

      // refreshDueDiligencePartnerData() already covers getCategoryNamesForMissingDoc(),
      // getActiveCategories() and getDocumentTaggingScores(true); calling them here as well
      // duplicated every one of those requests on load.
      this.refreshDueDiligencePartnerData();
    }

    // Keep LP Free access state in sync with chosen organization
    this.subscriptionAccessService
      .selectIsLpFreeAccess()
      .pipe(untilDestroyed(this))
      .subscribe((isLpFreeAccess) => {
        this.isLpFreeAccess = isLpFreeAccess;
        this.cr.markForCheck();
      });

    combineLatest([
      this.subscriptionAccessService.selectIsLpTemplateManagementLocked(),
      this.subscriptionAccessService.selectIsGpCustomTemplateLocked(),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isLpCustomTemplateLocked, isGpCustomTemplateLocked]) => {
        this.isLpCustomTemplateLocked = isLpCustomTemplateLocked;
        this.isGpCustomTemplateLocked = isGpCustomTemplateLocked;
        this.applyCustomTemplateTypeTierRestrictions();
        this.cr.markForCheck();
      });

    this.subscriptionAccessService
      .selectIsDdqRunsExhaustedFromProfile()
      .pipe(untilDestroyed(this))
      .subscribe((isExhausted) => {
        this.isDdqRunsExhausted = isExhausted;
        this.cr.markForCheck();
      });

    this.appStoreQuery
      .selectUserProfile()
      .pipe(untilDestroyed(this))
      .subscribe((profile) => {
        this.isAdvisorUser = !!profile?.isAdvisor;
        this.cr.markForCheck();
      });
  }

  private syncFundNameDialogState(): void {
    this.fundNameDialogRef?.componentInstance?.updateState({
      options: this.fundNameOptions,
      selectedFundName: this.selectedFundName,
      isLoading: this.isFundNameSuggestionsLoading,
    });
  }

  private getTemplateSelectionStorageKey(): string {
    return `dd-template-selection:${this.productuid || ''}`;
  }

  private persistTemplateSelectedOnce(): void {
    if (!this.productuid) {
      return;
    }

    localStorage.setItem(this.getTemplateSelectionStorageKey(), JSON.stringify({ hasSelectedTemplateOnce: true }));
  }

  private loadSavedTemplateSelectionFromStorage(): void {
    if (!this.productuid) {
      return;
    }

    const raw = (localStorage.getItem(this.getTemplateSelectionStorageKey()) || '').trim();
    if (!raw) {
      this.hasSelectedTemplateOnce = false;
      return;
    }

    try {
      const stored = JSON.parse(raw) as {
        hasSelectedTemplateOnce?: boolean;
        kind?: DdTemplateKind;
        templateId?: string;
        templateName?: string;
      };

      // Backward compatibility (old structure implied "selected once" if it contained a selection).
      const hasSelectedOnce = stored?.hasSelectedTemplateOnce === true || (!!stored?.kind && !!stored?.templateId);
      this.hasSelectedTemplateOnce = hasSelectedOnce;
      // Do not restore template controls from local storage.
      // The dropdown should only show the API-provided default template, if any.
      this.templateTypeControl.setValue(null, { emitEvent: false });
      this.templateNameControl.setValue(null, { emitEvent: false });
    } catch {
      this.hasSelectedTemplateOnce = false;
    }

    this.applyCustomTemplateTypeTierRestrictions();
  }

  public isDdCustomTemplateTypeDisabled(kind: DdTemplateKind): boolean {
    return isDdCustomTemplateTypeDisabled(this.isLpCustomTemplateLocked, this.isGpCustomTemplateLocked, kind);
  }

  private shouldPreserveApiDefaultTemplateSelection(): boolean {
    if (!this.pendingDefaultTemplateSelection) {
      return false;
    }

    const selectedTemplate = this.templateNameControl.value;
    if (!selectedTemplate) {
      return false;
    }

    return (
      this.templateTypeControl.value === this.pendingDefaultTemplateSelection.kind &&
      this.normalizeTemplateDetailsId(selectedTemplate.raw, selectedTemplate.id) === this.pendingDefaultTemplateSelection.id
    );
  }

  private applyCustomTemplateTypeTierRestrictions(): void {
    if (this.shouldPreserveApiDefaultTemplateSelection()) {
      return;
    }

    const coerced = coerceDdTemplateTypeForTierLock(
      this.isLpCustomTemplateLocked,
      this.isGpCustomTemplateLocked,
      this.templateTypeControl.value,
    );
    if (coerced === this.templateTypeControl.value) {
      return;
    }

    this.templateTypeControl.setValue(coerced, { emitEvent: false });
    this.templateNameControl.setValue(null, { emitEvent: false });
    this.templateNameOptions = [];
    if (this.hasSelectedTemplateOnce) {
      this.persistTemplateSelectedOnce();
    }
  }

  private persistTemplateSelection(kind: DdTemplateKind, option: DdTemplateOption): void {
    if (!this.productuid || !kind || !option?.id || !option?.name) {
      return;
    }

    const payload = JSON.stringify({
      hasSelectedTemplateOnce: true,
      kind,
      templateId: option.id,
      templateName: option.name,
    });
    localStorage.setItem(this.getTemplateSelectionStorageKey(), payload);
  }

  clearTemplateType(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.templateTypeControl.setValue(null);
    this.templateNameControl.setValue(null);
    this.templateNameOptions = [];

    // Keep "first-time unlock" true after the user has already selected once.
    if (this.hasSelectedTemplateOnce) {
      this.persistTemplateSelectedOnce();
    }

    this.cr.markForCheck();
  }

  clearTemplateName(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.templateNameControl.setValue(null);

    if (this.hasSelectedTemplateOnce) {
      this.persistTemplateSelectedOnce();
    }

    this.cr.markForCheck();
  }

  onTemplateTypeChanged(kind: DdTemplateKind | null): void {
    if (kind === 'custom' && this.isDdCustomTemplateTypeDisabled('custom')) {
      this.templateTypeControl.setValue(null, { emitEvent: false });
      this.templateNameControl.setValue(null);
      this.templateNameOptions = [];
      this.openCustomTemplateUpgradeDialog();
      this.cr.markForCheck();
      return;
    }

    if (!kind) {
      this.templateNameOptions = [];
      this.templateNameControl.setValue(null);
      if (this.hasSelectedTemplateOnce) {
        this.persistTemplateSelectedOnce();
      }
      this.cr.markForCheck();
      return;
    }

    // Reset name on type change, but do not re-disable launch once the user has already selected once.
    this.templateNameControl.setValue(null);
    this.templateNameOptions = this.allTemplateNameOptions.filter((option) => option.kind === kind);
    this.cr.markForCheck();
  }

  onTemplateNameChanged(option: DdTemplateOption | null): void {
    if (!option) {
      this.templateNameControl.setValue(null);
      if (this.hasSelectedTemplateOnce) {
        this.persistTemplateSelectedOnce();
      }
      this.cr.markForCheck();
      return;
    }

    const kind = this.templateTypeControl.value;
    if (!kind) {
      return;
    }

    this.templateNameControl.setValue(option);
    this.persistTemplateSelection(kind, option);
    this.hasSelectedTemplateOnce = true;
    this.ensureDefaultTemplateEntryForSelection('DD', kind, option)
      .pipe(take(1))
      .subscribe();
    this.cr.markForCheck();
  }

  public isTemplateSelectionRequiredForLaunch(): boolean {
    const selectedTemplateType = this.templateTypeControl.value;
    const selectedTemplateName = this.templateNameControl.value;

    return !!this.productuid && this.canShowDueDiligence === true && (!selectedTemplateType || !selectedTemplateName);
  }

  public isTemplateSelectionRequiredForUpload(): boolean {
    return this.isTemplateSelectionRequiredForLaunch();
  }

  private hasActiveAiTasks(): boolean {
    return (
      this.showBlinkerMessage ||
      this.isUploadingVectorStore ||
      this.isDocumentCategorize ||
      this.isScoringInProgress
    );
  }

  private hasUploadBlockingAiTask(): boolean {
    return this.showBlinkerMessage || this.isUploadingVectorStore || this.isScoringInProgress;
  }

  public isUploadDisabled(): boolean {
    return this.isReadOnly || this.isTemplateSelectionRequiredForUpload() || this.hasUploadBlockingAiTask();
  }

  public areDocumentsRequiredForLaunch(): boolean {
    return !this.isDataHasFile();
  }

  public isLaunchDisabled(): boolean {
    return (
      this.isAdvisorUser ||
      this.isLpFreeAccess ||
      this.isTemplateSelectionRequiredForLaunch() ||
      this.areDocumentsRequiredForLaunch() ||
      this.hasActiveAiTasks() ||
      this.isUploadingVectorStore ||
      this.isDocumentCategorize ||
      this.isProcessing ||
      this.hasInProgressDdRun ||
      this.hasInProgressImRun
    );
  }

  public getUploadTooltip(): string {
    if (this.isReadOnly) {
      return '';
    }

    if (this.hasUploadBlockingAiTask()) {
      return 'Documents are being analyzed';
    }

    if (this.isTemplateSelectionRequiredForUpload()) {
      return 'Select a template to enable upload';
    }

    return '';
  }

  public getLaunchTooltip(): string {
    if (this.isAdvisorUser) {
      return this.translate.instant('INTL_LAUNCH_DD_NOT_AVAILABLE_FOR_ADVISORS');
    }

    if (this.isLpFreeAccess) {
      return 'Upgrade required to access Due Diligence';
    }

    const missingRequirements: string[] = [];

    if (this.isTemplateSelectionRequiredForLaunch()) {
      missingRequirements.push('select a template');
    }

    if (this.areDocumentsRequiredForLaunch()) {
      missingRequirements.push('upload documents');
    }

    if (missingRequirements.length > 0) {
      return `${missingRequirements[0].charAt(0).toUpperCase()}${missingRequirements[0].slice(1)}${
        missingRequirements.length === 2 ? ` and ${missingRequirements[1]}` : ''
      } to enable launch`;
    }

    if (this.hasUploadBlockingAiTask()) {
      return 'Documents are being analyzed';
    }

    if (this.isUploadingVectorStore || this.isProcessing || this.isDocumentCategorize) {
      return 'Documents are still being analyzed';
    }

    if (this.hasInProgressDdRun || this.hasInProgressImRun) {
      return 'A process is currently running';
    }

    return 'Launch Due Diligence';
  }

  public getStatusTooltip(status: string | null | undefined): string {
    if (status === 'failed') {
      return 'Document analysis failed. Click to retry processing.';
    }
    if (status === 'completed') {
      return 'Document processed successfully';
    }
    if (status === 'notSupported') {
      return 'Document Not Supported';
    }

    return 'Document is being processed';
  }

  public isStatusDisable(status: string | null | undefined): boolean {
    if (status === 'notSupported') return true;
    return false;
  }

  private normalizeTemplateDetailsId(...candidates: any[]): number | string | null {
    for (const candidate of candidates) {
      const rawValue =
        candidate?.templatedetails_id ??
        candidate?.template_details?.templatedetails_id ??
        candidate?.template_details?.id ??
        candidate?.template_id ??
        candidate?.id ??
        candidate;
      const value = String(rawValue ?? '').trim();

      if (!value || /^[a-z]+-\d+$/i.test(value)) {
        continue;
      }

      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : value;
    }

    return null;
  }

  private getSelectedTemplateDetailsId(): number | string | null {
    const selectedTemplate = this.templateNameControl.value;
    return this.normalizeTemplateDetailsId(selectedTemplate?.raw, selectedTemplate?.id);
  }

  private getTemplateKindFromStandardFlag(value: unknown): DdTemplateKind | null {
    const normalizedValue = String(value ?? '').trim().toUpperCase();
    if (normalizedValue === 'Y') {
      return 'standard';
    }

    if (normalizedValue === 'N') {
      return 'custom';
    }

    return null;
  }

  public getDisplayedTemplateTypeLabel(): string {
    return this.templateTypeControl.value === 'standard'
      ? 'Standard'
      : this.templateTypeControl.value === 'custom'
        ? 'Custom'
        : '-';
  }

  public getDisplayedTemplateNameLabel(): string {
    return this.templateNameControl.value?.name?.trim() || '-';
  }

  public hasDefaultTemplateSelection(): boolean {
    return !!this.templateNameControl.value?.name?.trim();
  }

  public getDisplayedImTemplateTypeLabel(): string {
    return this.pendingImDefaultTemplateSelection?.kind === 'standard'
      ? 'Standard'
      : this.pendingImDefaultTemplateSelection?.kind === 'custom'
        ? 'Custom'
        : '-';
  }

  public getDisplayedImTemplateNameLabel(): string {
    return this.pendingImDefaultTemplateSelection?.name?.trim() || '-';
  }

  public hasImDefaultTemplateSelection(): boolean {
    return !!this.pendingImDefaultTemplateSelection?.name?.trim();
  }

  public shouldShowImTemplateSummary(): boolean {
    const scope = this.getDocumentTaggingScoresScope();
    return scope?.scopeType === 'CLIENT' || scope?.scopeType === 'PARTNER';
  }

  public selectTemplateTab(tab: DataroomTemplateTab): void {
    if (tab === 'IM' && !this.shouldShowImTemplateSummary()) {
      this.activeTemplateTab = 'DDQ';
      return;
    }

    this.activeTemplateTab = tab;
  }

  public isTemplateTabActive(tab: DataroomTemplateTab): boolean {
    if (tab === 'IM' && !this.shouldShowImTemplateSummary()) {
      return false;
    }

    return this.activeTemplateTab === tab;
  }

  public getActiveTemplateTypeLabel(): string {
    return this.activeTemplateTab === 'IM'
      ? this.getDisplayedImTemplateTypeLabel()
      : this.getDisplayedTemplateTypeLabel();
  }

  public getActiveTemplateNameLabel(): string {
    return this.activeTemplateTab === 'IM'
      ? this.getDisplayedImTemplateNameLabel()
      : this.getDisplayedTemplateNameLabel();
  }

  public hasActiveTemplateSelection(): boolean {
    return this.activeTemplateTab === 'IM'
      ? this.hasImDefaultTemplateSelection()
      : this.hasDefaultTemplateSelection();
  }

  public openActiveTemplateAssetMatchingModal(): void {
    if (this.activeTemplateTab === 'IM') {
      this.openImDefaultTemplateAssetMatchingModal();
      return;
    }

    this.openDefaultTemplateAssetMatchingModal();
  }

  public getDisplayedTemplateAssetClassLabel(): string {
    return this.getTemplateProductDisplayValue(
      this.productForm?.assetClass ??
        this.productForm?.assetClasses ??
        this.productForm?.fundType ??
        this.productForm?.asset_class,
    );
  }

  public getDisplayedTemplateSubAssetClassLabel(): string {
    return this.getTemplateProductDisplayValue(
      this.productForm?.subAssetClass ??
        this.productForm?.subAssetClasses ??
        this.productForm?.fundSubtype ??
        this.productForm?.asset_subclass,
    );
  }

  public openDefaultTemplateAssetMatchingModal(): void {
    this.showDefaultTemplateCreateHelperInModal = !this.hasDefaultTemplateSelection();
    this.hasResolvedDefaultTemplateOptionsInModal = false;
    this.defaultTemplateAssetMatchingDraft = {
      assetClassId: '',
      assetClass: this.savedDefaultTemplateAssetMatchingDraft?.assetClass || this.getDisplayedTemplateAssetClassLabel(),
      subAssetClassId: '',
      subAssetClass: this.savedDefaultTemplateAssetMatchingDraft?.subAssetClass || this.getDisplayedTemplateSubAssetClassLabel(),
      type: this.savedDefaultTemplateAssetMatchingDraft?.type || this.templateTypeControl.value || 'standard',
      templateId: '',
      templateName: this.savedDefaultTemplateAssetMatchingDraft?.templateName || (this.hasDefaultTemplateSelection() ? this.getDisplayedTemplateNameLabel() : ''),
      rowId: this.savedDefaultTemplateAssetMatchingDraft?.rowId ?? null,
      sourceScopeType: this.savedDefaultTemplateAssetMatchingDraft?.sourceScopeType ?? null,
    };
    this.loadDefaultTemplateAssetMatchingModalData();

    this.dialog.open(this.defaultTemplateAssetMatchingModal, {
      width: '760px',
      panelClass: 'default-template-asset-matching-dialog-container',
      autoFocus: false,
      restoreFocus: false,
    });
  }

  public cancelDefaultTemplateAssetMatchingModal(): void {
    this.defaultTemplateAssetMatchingDraft = null;
    this.showDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedDefaultTemplateOptionsInModal = false;
    this.dialog.closeAll();
  }

  public openImDefaultTemplateAssetMatchingModal(): void {
    this.showImDefaultTemplateCreateHelperInModal = !this.hasImDefaultTemplateSelection();
    this.hasResolvedImDefaultTemplateOptionsInModal = false;
    this.imDefaultTemplateAssetMatchingDraft = {
      assetClassId: '',
      assetClass: this.savedImDefaultTemplateAssetMatchingDraft?.assetClass || this.getDisplayedTemplateAssetClassLabel(),
      subAssetClassId: '',
      subAssetClass: this.savedImDefaultTemplateAssetMatchingDraft?.subAssetClass || this.getDisplayedTemplateSubAssetClassLabel(),
      type: this.savedImDefaultTemplateAssetMatchingDraft?.type || this.pendingImDefaultTemplateSelection?.kind || 'standard',
      templateId: '',
      templateName:
        this.savedImDefaultTemplateAssetMatchingDraft?.templateName ||
        (this.hasImDefaultTemplateSelection() ? this.getDisplayedImTemplateNameLabel() : ''),
      rowId: this.savedImDefaultTemplateAssetMatchingDraft?.rowId ?? null,
      sourceScopeType: this.savedImDefaultTemplateAssetMatchingDraft?.sourceScopeType ?? null,
    };
    this.loadImDefaultTemplateAssetMatchingModalData();

    this.dialog.open(this.imDefaultTemplateAssetMatchingModal, {
      width: '760px',
      panelClass: 'default-template-asset-matching-dialog-container',
      autoFocus: false,
      restoreFocus: false,
    });
  }

  public cancelImDefaultTemplateAssetMatchingModal(): void {
    this.imDefaultTemplateAssetMatchingDraft = null;
    this.showImDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedImDefaultTemplateOptionsInModal = false;
    this.dialog.closeAll();
  }

  public saveDefaultTemplateAssetMatchingModal(): void {
    const draft = this.defaultTemplateAssetMatchingDraft;
    if (!draft) {
      this.dialog.closeAll();
      return;
    }

    const partnerUid = this.getTemplateLookupPartnerUid();
    const scope = this.getDocumentTaggingScoresScope();
    const modifiedByUid = this.getCurrentUserId();
    const templateId = Number(draft.templateId);
    const assetClassId = Number(draft.assetClassId);
    const subAssetClassId = Number(draft.subAssetClassId);
    const mappingIds = this.getDefaultTemplateAssetMatchingSelectedMappingIds(draft.templateId);
    const isExistingDefaultTemplateMapping = draft.rowId !== null && draft.rowId !== undefined;

    if (
      !partnerUid ||
      !scope?.scopeUid ||
      !modifiedByUid ||
      !Number.isFinite(templateId) ||
      !Number.isFinite(assetClassId) ||
      !Number.isFinite(subAssetClassId) ||
      mappingIds.length === 0
    ) {
      this.snackBarService.open('Unable to save the default template asset matching.', 'error');
      return;
    }

    this.templateDDService
      .updateDefaultTemplate({
        id: draft.rowId,
        source_scope_type: draft.rowId ? draft.sourceScopeType : null,
        partner_uid: partnerUid,
        scope_type: scope.scopeType,
        scope_uid: scope.scopeUid,
        template_id: templateId,
        asset_class_ids: [assetClassId],
        asset_subclass_ids: [subAssetClassId],
        mapping_ids: mappingIds,
        modified_by_uid: modifiedByUid,
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (String(response?.status ?? '').trim().toUpperCase() !== 'SUCCESS') {
            this.snackBarService.open(String(response?.message ?? 'Failed to save default template asset matching.'), 'error');
            return;
          }

          this.savedDefaultTemplateAssetMatchingDraft = { ...draft };
          this.dialog.closeAll();
          this.hasLoadedTemplateOptions = false;
          this.loadTemplateOptions();
          if (isExistingDefaultTemplateMapping) {
            this.validateDdTemplateScoreBeforeLoadingScores(true, partnerUid);
          }
          this.startCurrentProductTemplateScoringJobPolling(response);
          if (!isExistingDefaultTemplateMapping) {
            this.getDocumentTaggingScores(false);
          }
          this.cr.markForCheck();
        },
        error: () => {
          this.snackBarService.open('Failed to save default template asset matching.', 'error');
        },
      });
  }

  public saveImDefaultTemplateAssetMatchingModal(): void {
    const draft = this.imDefaultTemplateAssetMatchingDraft;
    const partnerUid = this.getTemplateLookupPartnerUid();
    const scope = this.getDocumentTaggingScoresScope();
    const modifiedByUid = this.getCurrentUserId();
    const templateId = Number(draft?.templateId);
    const assetClassId = Number(draft?.assetClassId);
    const subAssetClassId = Number(draft?.subAssetClassId);
    const mappingIds = draft?.templateId ? this.getImDefaultTemplateAssetMatchingSelectedMappingIds(draft.templateId) : [];

    if (
      !draft ||
      !partnerUid ||
      !scope?.scopeUid ||
      !modifiedByUid ||
      !Number.isFinite(templateId) ||
      !Number.isFinite(assetClassId) ||
      !Number.isFinite(subAssetClassId) ||
      mappingIds.length === 0
    ) {
      this.snackBarService.open('Unable to save the default template asset matching.', 'error');
      return;
    }

    this.templateDDService
      .updateDefaultTemplate({
        id: draft.rowId,
        source_scope_type: draft.rowId ? draft.sourceScopeType : null,
        partner_uid: partnerUid,
        scope_type: scope.scopeType,
        scope_uid: scope.scopeUid,
        template_id: templateId,
        asset_class_ids: [assetClassId],
        asset_subclass_ids: [subAssetClassId],
        mapping_ids: mappingIds,
        modified_by_uid: modifiedByUid,
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (String(response?.status ?? '').trim().toUpperCase() !== 'SUCCESS') {
            this.snackBarService.open(String(response?.message ?? 'Failed to save default template asset matching.'), 'error');
            return;
          }

          this.savedImDefaultTemplateAssetMatchingDraft = { ...draft };
          this.dialog.closeAll();
          this.loadImDefaultTemplateSelection();
          this.cr.markForCheck();
        },
        error: () => {
          this.snackBarService.open('Failed to save default template asset matching.', 'error');
        },
      });
  }

  public onDefaultTemplateAssetClassChanged(assetClassId: string): void {
    if (!this.defaultTemplateAssetMatchingDraft) {
      return;
    }

    const selectedAssetClass = this.defaultTemplateAssetClassOptions.find((option) => option.value === assetClassId);
    this.defaultTemplateAssetMatchingDraft = {
      ...this.defaultTemplateAssetMatchingDraft,
      assetClassId,
      assetClass: selectedAssetClass?.label ?? '',
      subAssetClassId: '',
      subAssetClass: '',
      templateId: '',
      templateName: '',
      rowId: null,
      sourceScopeType: null,
    };
    this.defaultTemplateSubAssetClassOptions = [];
    this.defaultTemplateNameOptions = [];
    this.showDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedDefaultTemplateOptionsInModal = false;
    this.loadDefaultTemplateSubAssetClassOptions(assetClassId);
  }

  public onDefaultTemplateSubAssetClassChanged(subAssetClassId: string): void {
    if (!this.defaultTemplateAssetMatchingDraft) {
      return;
    }

    const selectedSubAssetClass = this.defaultTemplateSubAssetClassOptions.find((option) => option.value === subAssetClassId);
    this.defaultTemplateAssetMatchingDraft = {
      ...this.defaultTemplateAssetMatchingDraft,
      subAssetClassId,
      subAssetClass: selectedSubAssetClass?.label ?? '',
      templateId: '',
      templateName: '',
      rowId: null,
      sourceScopeType: null,
    };
    this.defaultTemplateNameOptions = [];
    this.showDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedDefaultTemplateOptionsInModal = false;
    this.loadDefaultTemplateNameOptions();
  }

  public onDefaultTemplateTypeChanged(type: DdTemplateKind): void {
    if (!this.defaultTemplateAssetMatchingDraft) {
      return;
    }

    this.defaultTemplateAssetMatchingDraft = {
      ...this.defaultTemplateAssetMatchingDraft,
      type,
      templateId: '',
      templateName: '',
      rowId: null,
      sourceScopeType: null,
    };
    this.defaultTemplateNameOptions = [];
    this.showDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedDefaultTemplateOptionsInModal = false;
    this.loadDefaultTemplateNameOptions();
  }

  public onDefaultTemplateNameChanged(templateId: string): void {
    if (!this.defaultTemplateAssetMatchingDraft) {
      return;
    }

    const selectedTemplate = this.defaultTemplateNameOptions.find((option) => option.value === templateId);
    this.defaultTemplateAssetMatchingDraft = {
      ...this.defaultTemplateAssetMatchingDraft,
      templateId,
      templateName: selectedTemplate?.label ?? '',
    };
  }

  public onImDefaultTemplateAssetClassChanged(assetClassId: string): void {
    if (!this.imDefaultTemplateAssetMatchingDraft) {
      return;
    }

    const selectedAssetClass = this.imDefaultTemplateAssetClassOptions.find((option) => option.value === assetClassId);
    this.imDefaultTemplateAssetMatchingDraft = {
      ...this.imDefaultTemplateAssetMatchingDraft,
      assetClassId,
      assetClass: selectedAssetClass?.label ?? '',
      subAssetClassId: '',
      subAssetClass: '',
      templateId: '',
      templateName: '',
      rowId: null,
      sourceScopeType: null,
    };
    this.imDefaultTemplateSubAssetClassOptions = [];
    this.imDefaultTemplateNameOptions = [];
    this.showImDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedImDefaultTemplateOptionsInModal = false;
    this.loadImDefaultTemplateSubAssetClassOptions(assetClassId);
  }

  public onImDefaultTemplateSubAssetClassChanged(subAssetClassId: string): void {
    if (!this.imDefaultTemplateAssetMatchingDraft) {
      return;
    }

    const selectedSubAssetClass = this.imDefaultTemplateSubAssetClassOptions.find((option) => option.value === subAssetClassId);
    this.imDefaultTemplateAssetMatchingDraft = {
      ...this.imDefaultTemplateAssetMatchingDraft,
      subAssetClassId,
      subAssetClass: selectedSubAssetClass?.label ?? '',
      templateId: '',
      templateName: '',
      rowId: null,
      sourceScopeType: null,
    };
    this.imDefaultTemplateNameOptions = [];
    this.showImDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedImDefaultTemplateOptionsInModal = false;
    this.loadImDefaultTemplateNameOptions();
  }

  public onImDefaultTemplateTypeChanged(type: DdTemplateKind): void {
    if (!this.imDefaultTemplateAssetMatchingDraft) {
      return;
    }

    this.imDefaultTemplateAssetMatchingDraft = {
      ...this.imDefaultTemplateAssetMatchingDraft,
      type,
      templateId: '',
      templateName: '',
      rowId: null,
      sourceScopeType: null,
    };
    this.imDefaultTemplateNameOptions = [];
    this.showImDefaultTemplateCreateHelperInModal = false;
    this.hasResolvedImDefaultTemplateOptionsInModal = false;
    this.loadImDefaultTemplateNameOptions();
  }

  public onImDefaultTemplateNameChanged(templateId: string): void {
    if (!this.imDefaultTemplateAssetMatchingDraft) {
      return;
    }

    const selectedTemplate = this.imDefaultTemplateNameOptions.find((option) => option.value === templateId);
    this.imDefaultTemplateAssetMatchingDraft = {
      ...this.imDefaultTemplateAssetMatchingDraft,
      templateId,
      templateName: selectedTemplate?.label ?? '',
    };
  }

  private getDefaultTemplateSelectionSource(defaultTemplate: any): any | null {
    if (Array.isArray(defaultTemplate)) {
      const ddTemplate =
        defaultTemplate.find(
          (item) => String(item?.template_type ?? '').trim().toUpperCase() === 'DD',
        ) ?? defaultTemplate[0];
      return ddTemplate ?? null;
    }

    return defaultTemplate ?? null;
  }

  private setDefaultTemplateSelectionFromScores(defaultTemplate: any): void {
    const selectedDefaultTemplate = this.getDefaultTemplateSelectionSource(defaultTemplate);
    const id = this.normalizeTemplateDetailsId(selectedDefaultTemplate);
    const name = String(selectedDefaultTemplate?.template_name ?? '').trim();
    const kind = this.getTemplateKindFromStandardFlag(selectedDefaultTemplate?.is_standard);

    if (!id || !name || !kind) {
      this.pendingDefaultTemplateSelection = null;
      return;
    }

    this.pendingDefaultTemplateSelection = { id, name, kind };
    this.applyPendingDefaultTemplateSelection();
  }

  private applyPendingDefaultTemplateSelection(): void {
    if (!this.pendingDefaultTemplateSelection) {
      return;
    }

    const pendingSelection = this.pendingDefaultTemplateSelection;
    const matchingOption =
      this.allTemplateNameOptions.find(
        (option) =>
          option.kind === pendingSelection.kind && this.normalizeTemplateDetailsId(option.raw, option.id) === pendingSelection.id,
      ) ??
      ({
        id: String(pendingSelection.id),
        name: pendingSelection.name,
        kind: pendingSelection.kind,
        raw: {
          templatedetails_id: pendingSelection.id,
          template_name: pendingSelection.name,
          is_standard: pendingSelection.kind === 'standard' ? 'Y' : 'N',
        },
      } as DdTemplateOption);

    this.templateTypeControl.setValue(pendingSelection.kind, { emitEvent: false });
    const templateNameOptions = this.allTemplateNameOptions.filter((option) => option.kind === pendingSelection.kind);
    this.templateNameOptions = templateNameOptions.some(
      (option) => this.normalizeTemplateDetailsId(option.raw, option.id) === pendingSelection.id,
    )
      ? templateNameOptions
      : [matchingOption, ...templateNameOptions];
    this.templateNameControl.setValue(matchingOption, { emitEvent: false });
    this.cr.markForCheck();
  }

  private loadImDefaultTemplateSelection(): void {
    if (!this.shouldShowImTemplateSummary()) {
      this.pendingImDefaultTemplateSelection = null;
      this.cr.markForCheck();
      return;
    }

    const partnerUid = this.getTemplateLookupPartnerUid();
    const scope = this.getDocumentTaggingScoresScope();

    if (!this.productuid || !partnerUid || !scope?.scopeUid) {
      this.pendingImDefaultTemplateSelection = null;
      this.cr.markForCheck();
      return;
    }

    this.resolveDdTemplateAssetIds()
      .pipe(
        switchMap((assetIds) => {
          if (!assetIds) {
            return of(null);
          }

          const payload = {
            partner_uid: partnerUid,
            scope_type: scope.scopeType,
            scope_uid: scope.scopeUid,
            template_type: 'IM' as const,
            asset_class_ids: assetIds.assetClassIds,
            asset_subclass_ids: assetIds.assetSubclassIds,
          };

          return forkJoin({
            standard: this.templateDDService.getTemplatesByAssetDdl({ ...payload, is_standard: 'Y' }),
            custom: this.templateDDService.getTemplatesByAssetDdl({ ...payload, is_standard: 'N' }),
            assetIds: of(assetIds),
          });
        }),
        take(1),
        catchError((error) => {
          console.error('Failed to load IM default template selection', error);
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (!result || !scope?.scopeUid) {
          this.pendingImDefaultTemplateSelection = null;
          this.cr.markForCheck();
          return;
        }

        const matchingStandardRow = this.findMatchingDefaultTemplateRow(
          this.getExistingDefaultTemplateRowsFromAssetResponse(result.standard),
          scope,
          result.assetIds,
        );
        const matchingCustomRow = this.findMatchingDefaultTemplateRow(
          this.getExistingDefaultTemplateRowsFromAssetResponse(result.custom),
          scope,
          result.assetIds,
        );
        const matchedKind: DdTemplateKind | null = matchingStandardRow
          ? 'standard'
          : matchingCustomRow
            ? 'custom'
            : null;
        const matchedRow = matchingStandardRow ?? matchingCustomRow ?? null;
        const matchedId = this.normalizeTemplateDetailsId(matchedRow?.template_id, matchedRow?.templatedetails_id);
        const matchedName = String(matchedRow?.template_name ?? '').trim();

        this.pendingImDefaultTemplateSelection =
          matchedKind && matchedId && matchedName
            ? {
                id: matchedId,
                name: matchedName,
                kind: matchedKind,
              }
            : null;
        this.savedImDefaultTemplateAssetMatchingDraft =
          this.pendingImDefaultTemplateSelection && matchedRow
            ? {
                assetClassId: String(result.assetIds.assetClassIds[0] ?? ''),
                assetClass: '',
                subAssetClassId: String(result.assetIds.assetSubclassIds[0] ?? ''),
                subAssetClass: '',
                type: this.pendingImDefaultTemplateSelection.kind,
                templateId: String(this.pendingImDefaultTemplateSelection.id),
                templateName: this.pendingImDefaultTemplateSelection.name,
                rowId: Number(matchedRow?.id ?? null),
                sourceScopeType: this.normalizeDefaultTemplateScopeType(matchedRow?.scope_type),
              }
            : null;
        this.cr.markForCheck();
      });
  }

  private loadTemplateOptions(): void {
    if (this.hasLoadedTemplateOptions || !this.productuid) {
      return;
    }

    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      this.appStoreQuery
        .selectUser()
        .pipe(
          filter((user) => Boolean(user?.id)),
          take(1),
        )
        .subscribe(() => this.loadTemplateOptions());
      return;
    }

    this.hasLoadedTemplateOptions = true;
    this.isTemplateOptionsLoading = true;

    const activeClientId = this.clientQuery?.getActiveId?.() ?? null;
    setStoredTemplateClientContextClientId(activeClientId);

    const scope = this.getDocumentTaggingScoresScope();

    this.resolveDdTemplateAssetIds()
      .pipe(
        switchMap((assetIds) => {
          if (!assetIds) {
            this.hasLoadedTemplateOptions = false;
            return of(null);
          }

          const partnerUid = this.getTemplateLookupPartnerUid();
          if (!partnerUid || !scope?.scopeUid) {
            this.hasLoadedTemplateOptions = false;
            return of(null);
          }

          return forkJoin({
            standard: this.templateDDService.getTemplatesByAssetDdl({
              partner_uid: partnerUid,
              scope_type: scope.scopeType,
              scope_uid: scope.scopeUid,
              is_standard: 'Y',
              template_type: 'DD',
              asset_class_ids: assetIds.assetClassIds,
              asset_subclass_ids: assetIds.assetSubclassIds,
            }),
            custom: this.templateDDService.getTemplatesByAssetDdl({
              partner_uid: partnerUid,
              scope_type: scope.scopeType,
              scope_uid: scope.scopeUid,
              is_standard: 'N',
              template_type: 'DD',
              asset_class_ids: assetIds.assetClassIds,
              asset_subclass_ids: assetIds.assetSubclassIds,
            }),
          });
        }),
        take(1),
        finalize(() => {
          this.isTemplateOptionsLoading = false;
          this.cr.markForCheck();
        }),
        catchError((error) => {
          console.error('Failed to load template options', error);
          return of(null);
        }),
      )
      .subscribe((res) => {
        const standard = this.getTemplateRowsFromAssetResponse(res?.standard);
        const custom = this.getTemplateRowsFromAssetResponse(res?.custom);

        const mapOption = (kind: DdTemplateKind, item: any, index: number): DdTemplateOption | null => {
          const rawName =
            item?.template_details?.template_name ?? item?.template_name ?? item?.name ?? item?.title ?? '';
          const rawId =
            item?.templatedetails_id ??
            item?.template_details?.templatedetails_id ??
            item?.id ??
            item?.template_id ??
            '';
          const name = String(rawName || '').trim();
          const id = String(rawId || '').trim() || `${kind}-${index + 1}`;

          if (!name) return null;
          return { id, name, kind, raw: item };
        };

        const options = [
          ...standard.map((item: any, i: number) => mapOption('standard', item, i)),
          ...custom.map((item: any, i: number) => mapOption('custom', item, i)),
        ].filter(Boolean) as DdTemplateOption[];

        this.allTemplateNameOptions = options;
        const selectedKind = this.templateTypeControl.value;
        this.templateNameOptions = selectedKind
          ? this.allTemplateNameOptions.filter((opt) => opt.kind === selectedKind)
          : [];

        const selectedOption = this.templateNameControl.value;
        if (selectedKind && selectedOption?.id) {
          const matchingOption = this.allTemplateNameOptions.find(
            (option) => option.kind === selectedKind && option.id === selectedOption.id,
          );
          if (matchingOption) {
            this.templateNameControl.setValue(matchingOption, { emitEvent: false });
          }
        }

        this.applyPendingDefaultTemplateSelection();
        this.cr.markForCheck();
      });
  }

  private updateDisplayedColumns(isCompact: boolean): void {
    if (isCompact) {
      this.displayedColumns = this.canShowDueDiligence
        ? ['select', 'name', ...(this.productuid ? ['category'] : []), 'menu']
        : ['select', 'name', 'menu'];
    } else {
      this.displayedColumns = this.canShowDueDiligence
        ? ['select', 'name', 'modifiedAt', ...(this.productuid ? ['category'] : []), 'file.size', 'menu']
        : ['select', 'name', 'modifiedAt', 'file.size', 'menu'];
    }
  }

  private resolveEmittentCompanyUidForTemplateLookup(): string | null {
    return this.emittentuid || this.emittentId || null;
  }

  private readPartnerUidFromKeys(): string | null {
    try {
      const raw = localStorage.getItem('keys');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { partneruid?: string };
      return parsed?.partneruid ?? null;
    } catch {
      return null;
    }
  }

  private readActiveThemePartnerUid(): string | null {
    try {
      const raw = localStorage.getItem('activeTheme');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partner;
      return parsed?.uid ?? null;
    } catch {
      return null;
    }
  }

  private normalizeOriginUrl(url: string | null | undefined): string | null {
    if (!url?.trim()) {
      return null;
    }
    return url.trim().replace(/\/+$/, '');
  }

  /**
   * Resolve DD partner for API calls when org changes — mirrors OrganizationSelectorService partner resolution.
   */
  private resolvedDueDiligencePartnerUid(
    organization: Organization | null | undefined,
    keysPartnerUid: string | null | undefined,
  ): Partner | null {
    const partners = organization?.partners;
    if (!partners?.length) {
      return keysPartnerUid ? ({ uid: keysPartnerUid } as Partner) : null;
    }

    const currentOrigin = this.normalizeOriginUrl(
      typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}` : null,
    );
    const byDomain = partners.find((p) => this.normalizeOriginUrl(p?.domain) === currentOrigin);
    if (byDomain) {
      return byDomain;
    }

    const themeUid = this.readActiveThemePartnerUid();
    if (themeUid) {
      const byTheme = partners.find((p) => p?.uid === themeUid);
      if (byTheme) {
        return byTheme;
      }
    }

    if (keysPartnerUid) {
      const byKeys = partners.find((p) => p?.uid === keysPartnerUid);
      if (byKeys) {
        return byKeys;
      }
    }

    if (partners.length === 1) {
      return partners[0];
    }

    const dueDiligencePartners = partners.filter((p) => p?.isDueDiligence === true);
    if (dueDiligencePartners.length === 1) {
      return dueDiligencePartners[0];
    }

    return null;
  }

  private syncStoredTemplateClientIdFromContext(): void {
    const activeClientId = this.clientQuery?.getActiveId?.() ?? null;
    const routeClientId =
      extractCreatedForUserIdFromRoute(this.router.url) ??
      extractCreatedForUserIdFromRoute(history.state?.returnUrl) ??
      extractCreatedForUserIdFromRoute(this.appStoreQuery?.getValue?.().currentReturnUrl) ??
      null;

    const clientContextId = activeClientId ?? routeClientId;

    if (clientContextId) {
      setStoredTemplateClientContextClientId(clientContextId);
      return;
    }

    clearStoredTemplateClientContextClientId();
  }

  private getTemplateLookupIsClientFlag(activeClientId: string | null): 'Y' | 'N' {
    return isTemplateClientContext({
      currentUserType: this.getCurrentUserType(),
      activeClientId,
      storedClientId: getStoredTemplateClientContextClientId(),
      currentRoute: this.router.url,
      historyReturnUrl: history.state?.returnUrl,
      storedReturnUrl: this.appStoreQuery?.getValue?.().currentReturnUrl,
    })
      ? 'Y'
      : 'N';
  }

  private getProcessQueueOrgName(): string {
    const activeClientId = this.clientQuery?.getActiveId?.() ?? null;
    const storedClientId = getStoredTemplateClientContextClientId();

    return resolveProcessQueueOrgName(
      buildProcessQueueOrgNameBaseContext({
        emittentCompanyName: this.emittentCompanyName,
        currentUserType: this.getCurrentUserType(),
        activeClientId,
        storedClientId,
        currentRoute: this.router.url,
        historyReturnUrl: history.state?.returnUrl,
        storedReturnUrl: this.appStoreQuery?.getValue?.().currentReturnUrl,
        activeClient: (this.clientQuery?.getActive?.() as Client | undefined) ?? null,
        clientQuery: this.clientQuery,
        loggedInUser: this.appStoreQuery.getValue().user ?? null,
        partnerUid: this.getTemplateLookupPartnerUid(),
        organizationQuery: this.organizationQuery,
      }),
    );
  }

  private getCurrentUserId(): string | null {
    return this.appStoreQuery.getValue().user?.id || localStorage.getItem('uid');
  }

  private getDocumentTaggingScoresScope(): { scopeType: 'PARTNER' | 'EMITTENT' | 'CLIENT'; scopeUid: string } | null {
    const activeClientId = this.clientQuery?.getActiveId?.() ?? null;
    const clientScopeUid =
      this.appStoreQuery.getValue().user?.client?.uid ??
      activeClientId ??
      extractCreatedForUserIdFromRoute(this.router.url) ??
      extractCreatedForUserIdFromRoute(history.state?.returnUrl) ??
      extractCreatedForUserIdFromRoute(this.appStoreQuery?.getValue?.().currentReturnUrl) ??
      getStoredTemplateClientContextClientId() ??
      null;

    if (clientScopeUid && this.getTemplateLookupIsClientFlag(activeClientId) === 'Y') {
      return { scopeType: 'CLIENT', scopeUid: clientScopeUid };
    }

    const emittentUid = this.resolveEmittentCompanyUidForTemplateLookup();
    if (emittentUid) {
      return { scopeType: 'EMITTENT', scopeUid: emittentUid };
    }

    const partnerUid = this.getCurrentPartnerUid();
    return partnerUid ? { scopeType: 'PARTNER', scopeUid: partnerUid } : null;
  }

  private getCurrentPartnerUid(): string | null {
    return this.partneruid || this.readActiveThemePartnerUid() || null;
  }

  private getTemplateLookupPartnerUid(): string | null {
    const currentUser = this.appStoreQuery.getValue().user as
      | {
          ddPartner?: { uid?: string | null } | null;
          partnerAdminPartners?: Array<{ uid?: string | null }> | null;
        }
      | null
      | undefined;
    let keysPartnerUid: string | null = null;
    let activeThemePartnerUid: string | null = null;

    try {
      const rawKeys = localStorage.getItem('keys');
      keysPartnerUid = rawKeys ? JSON.parse(rawKeys)?.partneruid ?? null : null;
    } catch {
      keysPartnerUid = null;
    }

    try {
      const rawActiveTheme = localStorage.getItem('activeTheme');
      activeThemePartnerUid = rawActiveTheme ? JSON.parse(rawActiveTheme)?.uid ?? null : null;
    } catch {
      activeThemePartnerUid = null;
    }

    const candidates = [
      activeThemePartnerUid,
      keysPartnerUid,
      currentUser?.ddPartner?.uid ?? null,
      currentUser?.partnerAdminPartners?.[0]?.uid ?? null,
      this.partneruid,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return null;
  }

  private getCurrentUserType(): string {
    const currentUser = this.appStoreQuery.getValue().user;
    const explicitUserType = String(
      currentUser?.userType ??
        (currentUser as { user_type?: string } | null)?.user_type ??
        localStorage.getItem('userType') ??
        '',
    )
      .trim()
      .toLowerCase();

    if (explicitUserType) {
      return explicitUserType;
    }

    if (currentUser?.isSuperUser) {
      return 'superuser';
    }
    if (currentUser?.isStaff) {
      return 'staff';
    }
    if (currentUser?.partnerAdmin?.uid) {
      return 'partner_admin';
    }
    if (currentUser?.isAnalystUser) {
      return 'analyst';
    }
    if (currentUser?.isClient) {
      return 'client';
    }
    if (currentUser?.isAdvisor) {
      return 'advisor';
    }

    const effectiveOrganizationType =
      this.organizationContextService.getEffectiveOrganizationTypeSync?.() ??
      this.organizationQuery.getValue()?.chosenOrganizationType ??
      this.organizationQuery.getActive()?.type ??
      null;

    if (effectiveOrganizationType === 'emittent') {
      return 'emittent_admin';
    }
    if (effectiveOrganizationType === 'investor') {
      return 'investor_admin';
    }

    return '';
  }

  private toTemplateProductFiniteNumber(value: unknown): number | null {
    const normalizedValue = Number(value);
    return Number.isFinite(normalizedValue) ? normalizedValue : null;
  }

  private getSelectedTemplateMappingIds(
    selectedTemplate: DdTemplateOption | null = this.templateNameControl.value,
    selectedTemplateKind: DdTemplateKind | null = this.templateTypeControl.value,
  ): number[] {
    const normalizeMappingIds = (mappingIds: unknown): number[] =>
      Array.isArray(mappingIds)
        ? mappingIds.map((mappingId: unknown) => Number(mappingId)).filter((mappingId: number) => Number.isFinite(mappingId))
        : [];

    const rawMappingIds = normalizeMappingIds(selectedTemplate?.raw?.mapping_ids);
    if (rawMappingIds.length > 0) {
      return rawMappingIds;
    }

    const selectedTemplateId = this.normalizeTemplateDetailsId(selectedTemplate?.raw, selectedTemplate?.id);
    const matchingOption = this.allTemplateNameOptions.find((option) => {
      if (selectedTemplateKind && option.kind !== selectedTemplateKind) {
        return false;
      }

      return this.normalizeTemplateDetailsId(option.raw, option.id) === selectedTemplateId;
    });

    return normalizeMappingIds(matchingOption?.raw?.mapping_ids);
  }

  private extractTemplateAssetIds(response: any): { assetClassIds: number[]; assetSubclassIds: number[] } | null {
    const raw =
      (Array.isArray(response?.data) ? response.data[0] : null) ??
      (Array.isArray(response) ? response[0] : null) ??
      response?.data ??
      response ??
      null;

    if (!raw) {
      return null;
    }

    const assetClassId = this.toTemplateProductFiniteNumber(
      raw?.assetclass_id ?? raw?.asset_class_id ?? raw?.assetClassId ?? raw?.asset_class_ids?.[0],
    );
    const assetSubclassId = this.toTemplateProductFiniteNumber(
      raw?.subasset_class_id ?? raw?.asset_subclass_id ?? raw?.assetSubclassId ?? raw?.asset_subclass_ids?.[0],
    );

    if (!assetClassId || !assetSubclassId) {
      return null;
    }

    return {
      assetClassIds: [assetClassId],
      assetSubclassIds: [assetSubclassId],
    };
  }

  private resolveDdTemplateAssetIds(): Observable<{ assetClassIds: number[]; assetSubclassIds: number[] } | null> {
    if (!this.productuid) {
      return of(null);
    }

    return this.templateDDService.getAssetDetails({ product_uid: this.productuid }).pipe(
      take(1),
      map((response) => this.extractTemplateAssetIds(response)),
    );
  }

  private getTemplateProductDisplayValue(value: unknown): string {
    if (Array.isArray(value)) {
      const labels = value
        .map((item) => {
          if (typeof item === 'string' || typeof item === 'number') {
            return String(item).trim();
          }

          if (item && typeof item === 'object') {
            const record = item as Record<string, unknown>;
            return String(
              record.label ??
                record.name ??
                record.title ??
                record.value ??
                record.assetClassName ??
                record.subAssetClassName ??
                '',
            ).trim();
          }

          return '';
        })
        .filter((label) => !!label);

      return labels.length ? labels.join(', ') : '-';
    }

    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      return String(
        record.label ??
          record.name ??
          record.title ??
          record.value ??
          record.assetClassName ??
          record.subAssetClassName ??
          '',
      ).trim() || '-';
    }

    return String(value ?? '').trim() || '-';
  }

  private loadDefaultTemplateAssetMatchingModalData(): void {
    const draft = this.defaultTemplateAssetMatchingDraft;
    if (!draft) {
      return;
    }

    this.isDefaultTemplateAssetMatchingLoading = true;
    this.defaultTemplateAssetClassOptions = [];
    this.defaultTemplateSubAssetClassOptions = [];
    this.defaultTemplateNameOptions = [];

    forkJoin({
      assetClasses: this.templateDDService.getAssetClasses({ listName: 'Assetclass' }).pipe(take(1)),
      assetIds: this.resolveDdTemplateAssetIds().pipe(take(1)),
    })
      .pipe(
        finalize(() => {
          this.isDefaultTemplateAssetMatchingLoading = false;
          this.cr.markForCheck();
        }),
        catchError((error) => {
          console.error('Failed to load default template asset matching modal data', error);
          return of(null);
        }),
      )
      .subscribe((result) => {
        const assetClassRows = Array.isArray(result?.assetClasses) ? result.assetClasses : [];
        this.defaultTemplateAssetClassOptions = assetClassRows
          .map((item: { assetclass?: string; assetclass_id?: number | string }) => {
            const label = String(item?.assetclass ?? '').trim();
            const value = String(item?.assetclass_id ?? '').trim();
            if (!label || !value) {
              return null;
            }

            return {
              label: this.formatTemplateApiDisplayValue(label),
              value,
            } as DefaultTemplateAssetMatchingOption;
          })
          .filter((item): item is DefaultTemplateAssetMatchingOption => !!item);

        const assetClassId = String(result?.assetIds?.assetClassIds?.[0] ?? '').trim();
        const subAssetClassId = String(result?.assetIds?.assetSubclassIds?.[0] ?? '').trim();
        const selectedAssetClass = this.defaultTemplateAssetClassOptions.find((option) => option.value === assetClassId);

        this.defaultTemplateAssetMatchingDraft = {
          ...this.defaultTemplateAssetMatchingDraft!,
          assetClassId,
          assetClass: selectedAssetClass?.label ?? this.defaultTemplateAssetMatchingDraft!.assetClass,
          subAssetClassId,
          type: this.defaultTemplateAssetMatchingDraft!.type || 'standard',
        };

        if (!assetClassId) {
          return;
        }

        this.loadDefaultTemplateSubAssetClassOptions(assetClassId, subAssetClassId);
      });
  }

  private loadDefaultTemplateSubAssetClassOptions(assetClassId: string, preferredSubAssetClassId = ''): void {
    if (!assetClassId) {
      this.defaultTemplateSubAssetClassOptions = [];
      this.defaultTemplateNameOptions = [];
      this.cr.markForCheck();
      return;
    }

    this.templateDDService
      .getSubAssetClasses({
        listName: 'Subassetclass',
        Assetclassid: [Number(assetClassId)],
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const rows = Array.isArray(response) ? response : [];
          this.defaultTemplateSubAssetClassOptions = rows
            .flatMap((item: {
              assetclass_id?: number | string;
              subclasses?: { subasset_class_id?: number | string; subasset_class_name?: string }[];
            }) =>
              Array.isArray(item?.subclasses)
                ? item.subclasses.map((subclass) => {
                    const label = String(subclass?.subasset_class_name ?? '').trim();
                    const value = String(subclass?.subasset_class_id ?? '').trim();
                    if (!label || !value) {
                      return null;
                    }

                    return {
                      label: this.formatTemplateApiDisplayValue(label),
                      value,
                    } as DefaultTemplateAssetMatchingOption;
                  })
                : [],
            )
            .filter((item): item is DefaultTemplateAssetMatchingOption => !!item);

          const selectedSubAsset =
            this.defaultTemplateSubAssetClassOptions.find((option) => option.value === preferredSubAssetClassId) ??
            this.defaultTemplateSubAssetClassOptions[0] ??
            null;

          this.defaultTemplateAssetMatchingDraft = this.defaultTemplateAssetMatchingDraft
            ? {
                ...this.defaultTemplateAssetMatchingDraft,
                subAssetClassId: selectedSubAsset?.value ?? '',
                subAssetClass: selectedSubAsset?.label ?? '',
              }
            : null;

          this.loadDefaultTemplateNameOptions();
        },
        error: () => {
          this.defaultTemplateSubAssetClassOptions = [];
          this.defaultTemplateNameOptions = [];
          this.cr.markForCheck();
        },
      });
  }

  private loadDefaultTemplateNameOptions(): void {
    const draft = this.defaultTemplateAssetMatchingDraft;
    if (!draft?.assetClassId || !draft.subAssetClassId) {
      this.defaultTemplateNameOptions = [];
      this.showDefaultTemplateCreateHelperInModal = false;
      this.hasResolvedDefaultTemplateOptionsInModal = false;
      this.cr.markForCheck();
      return;
    }

    const partnerUid = this.getTemplateLookupPartnerUid();
    const scope = this.getDocumentTaggingScoresScope();
    if (!partnerUid || !scope?.scopeUid) {
      this.defaultTemplateNameOptions = [];
      this.showDefaultTemplateCreateHelperInModal = false;
      this.hasResolvedDefaultTemplateOptionsInModal = false;
      this.cr.markForCheck();
      return;
    }

    const payload = {
      partner_uid: partnerUid,
      scope_type: scope.scopeType,
      scope_uid: scope.scopeUid,
      is_standard: draft.type === 'standard' ? ('Y' as const) : ('N' as const),
      template_type: 'DD' as const,
      asset_class_ids: [Number(draft.assetClassId)],
      asset_subclass_ids: [Number(draft.subAssetClassId)],
    };

    this.templateDDService
      .getTemplatesByAssetDdl(payload)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const templateRows = this.getTemplateRowsFromAssetResponse(response);
          this.defaultTemplateNameOptions = templateRows
            .map((item: any) => {
              const label = String(
                item?.template_details?.template_name ??
                  item?.template_name ??
                  item?.name ??
                  item?.title ??
                  '',
              ).trim();
              const value = String(
                item?.templatedetails_id ??
                  item?.template_details?.templatedetails_id ??
                  item?.template_details?.id ??
                  item?.template_id ??
                  item?.id ??
                  '',
              ).trim();

              if (!label || !value) {
                return null;
              }

              return {
                label,
                value,
                mappingIds: Array.isArray(item?.mapping_ids)
                  ? item.mapping_ids.map((mappingId: unknown) => Number(mappingId)).filter((mappingId: number) => Number.isFinite(mappingId))
                  : [],
              } as DefaultTemplateAssetMatchingOption;
            })
            .filter((item): item is DefaultTemplateAssetMatchingOption => !!item);

          this.showDefaultTemplateCreateHelperInModal = this.defaultTemplateNameOptions.length === 0;
          this.hasResolvedDefaultTemplateOptionsInModal = true;

          const matchingDefaultRow = this.findMatchingDefaultTemplateRow(
            this.getExistingDefaultTemplateRowsFromAssetResponse(response),
            scope,
            {
              assetClassIds: [Number(draft.assetClassId)],
              assetSubclassIds: [Number(draft.subAssetClassId)],
            },
          );

          const currentTemplateId = String(
            matchingDefaultRow?.template_id ??
              draft.templateId ??
              this.getSelectedTemplateDetailsId() ??
              this.defaultTemplateNameOptions[0]?.value ??
              '',
          ).trim();
          const selectedTemplate = this.defaultTemplateNameOptions.find((option) => option.value === currentTemplateId) ?? null;

          this.defaultTemplateAssetMatchingDraft = this.defaultTemplateAssetMatchingDraft
            ? {
                ...this.defaultTemplateAssetMatchingDraft,
                templateId: selectedTemplate?.value ?? '',
                templateName: selectedTemplate?.label ?? '',
                rowId: matchingDefaultRow ? Number(matchingDefaultRow.id ?? null) : null,
                sourceScopeType: this.normalizeDefaultTemplateScopeType(matchingDefaultRow?.scope_type),
              }
            : null;
          this.cr.markForCheck();
        },
        error: () => {
          this.defaultTemplateNameOptions = [];
          this.showDefaultTemplateCreateHelperInModal = !this.hasDefaultTemplateSelection();
          this.hasResolvedDefaultTemplateOptionsInModal = true;
          this.cr.markForCheck();
        },
      });
  }

  private findMatchingDefaultTemplateRow(
    rows: any[],
    scope: { scopeType: 'PARTNER' | 'EMITTENT' | 'CLIENT'; scopeUid: string },
    assetIds: { assetClassIds: number[]; assetSubclassIds: number[] },
  ): any | null {
    const expectedAssetClassIds = [...assetIds.assetClassIds].sort((a, b) => a - b);
    const expectedAssetSubclassIds = [...assetIds.assetSubclassIds].sort((a, b) => a - b);

    return (
      rows.find((row) => {
        const rowScopeType = String(row?.scope_type ?? '').trim().toUpperCase();
        const rowScopeUid = String(row?.scope_uid ?? '').trim();
        const rowAssetClassIds = this.normalizeTemplateNumericArray(row?.asset_ids ?? row?.asset_class_ids);
        const rowAssetSubclassIds = this.normalizeTemplateNumericArray(row?.sub_asset_ids ?? row?.asset_subclass_ids);

        return (
          rowScopeType === scope.scopeType &&
          rowScopeUid === scope.scopeUid &&
          rowAssetClassIds.length === expectedAssetClassIds.length &&
          rowAssetClassIds.every((value, index) => value === expectedAssetClassIds[index]) &&
          expectedAssetSubclassIds.every((value) => rowAssetSubclassIds.includes(value))
        );
      }) ?? null
    );
  }

  private getDefaultTemplateAssetMatchingSelectedMappingIds(templateId: string): number[] {
    return this.defaultTemplateNameOptions.find((option) => option.value === templateId)?.mappingIds ?? [];
  }

  private getImDefaultTemplateAssetMatchingSelectedMappingIds(templateId: string): number[] {
    return this.imDefaultTemplateNameOptions.find((option) => option.value === templateId)?.mappingIds ?? [];
  }

  private loadImDefaultTemplateAssetMatchingModalData(): void {
    const draft = this.imDefaultTemplateAssetMatchingDraft;
    if (!draft) {
      return;
    }

    this.isImDefaultTemplateAssetMatchingLoading = true;
    this.imDefaultTemplateAssetClassOptions = [];
    this.imDefaultTemplateSubAssetClassOptions = [];
    this.imDefaultTemplateNameOptions = [];

    forkJoin({
      assetClasses: this.templateDDService.getAssetClasses({ listName: 'Assetclass' }).pipe(take(1)),
      assetIds: this.resolveDdTemplateAssetIds().pipe(take(1)),
    })
      .pipe(
        finalize(() => {
          this.isImDefaultTemplateAssetMatchingLoading = false;
          this.cr.markForCheck();
        }),
        catchError((error) => {
          console.error('Failed to load IM default template asset matching modal data', error);
          return of(null);
        }),
      )
      .subscribe((result) => {
        const assetClassRows = Array.isArray(result?.assetClasses) ? result.assetClasses : [];
        this.imDefaultTemplateAssetClassOptions = assetClassRows
          .map((item: { assetclass?: string; assetclass_id?: number | string }) => {
            const label = String(item?.assetclass ?? '').trim();
            const value = String(item?.assetclass_id ?? '').trim();
            if (!label || !value) {
              return null;
            }

            return {
              label: this.formatTemplateApiDisplayValue(label),
              value,
            } as DefaultTemplateAssetMatchingOption;
          })
          .filter((item): item is DefaultTemplateAssetMatchingOption => !!item);

        const assetClassId = String(result?.assetIds?.assetClassIds?.[0] ?? '').trim();
        const subAssetClassId = String(result?.assetIds?.assetSubclassIds?.[0] ?? '').trim();
        const selectedAssetClass = this.imDefaultTemplateAssetClassOptions.find((option) => option.value === assetClassId);

        this.imDefaultTemplateAssetMatchingDraft = {
          ...this.imDefaultTemplateAssetMatchingDraft!,
          assetClassId,
          assetClass: selectedAssetClass?.label ?? this.imDefaultTemplateAssetMatchingDraft!.assetClass,
          subAssetClassId,
          type: this.imDefaultTemplateAssetMatchingDraft!.type || 'standard',
        };

        if (!assetClassId) {
          return;
        }

        this.loadImDefaultTemplateSubAssetClassOptions(assetClassId, subAssetClassId);
      });
  }

  private loadImDefaultTemplateSubAssetClassOptions(assetClassId: string, preferredSubAssetClassId = ''): void {
    if (!assetClassId) {
      this.imDefaultTemplateSubAssetClassOptions = [];
      this.imDefaultTemplateNameOptions = [];
      this.cr.markForCheck();
      return;
    }

    this.templateDDService
      .getSubAssetClasses({
        listName: 'Subassetclass',
        Assetclassid: [Number(assetClassId)],
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const rows = Array.isArray(response) ? response : [];
          this.imDefaultTemplateSubAssetClassOptions = rows
            .flatMap((item: {
              assetclass_id?: number | string;
              subclasses?: { subasset_class_id?: number | string; subasset_class_name?: string }[];
            }) =>
              Array.isArray(item?.subclasses)
                ? item.subclasses.map((subclass) => {
                    const label = String(subclass?.subasset_class_name ?? '').trim();
                    const value = String(subclass?.subasset_class_id ?? '').trim();
                    if (!label || !value) {
                      return null;
                    }

                    return {
                      label: this.formatTemplateApiDisplayValue(label),
                      value,
                    } as DefaultTemplateAssetMatchingOption;
                  })
                : [],
            )
            .filter((item): item is DefaultTemplateAssetMatchingOption => !!item);

          const selectedSubAsset =
            this.imDefaultTemplateSubAssetClassOptions.find((option) => option.value === preferredSubAssetClassId) ??
            this.imDefaultTemplateSubAssetClassOptions[0] ??
            null;

          this.imDefaultTemplateAssetMatchingDraft = this.imDefaultTemplateAssetMatchingDraft
            ? {
                ...this.imDefaultTemplateAssetMatchingDraft,
                subAssetClassId: selectedSubAsset?.value ?? '',
                subAssetClass: selectedSubAsset?.label ?? '',
              }
            : null;

          this.loadImDefaultTemplateNameOptions();
        },
        error: () => {
          this.imDefaultTemplateSubAssetClassOptions = [];
          this.imDefaultTemplateNameOptions = [];
          this.cr.markForCheck();
        },
      });
  }

  private loadImDefaultTemplateNameOptions(): void {
    const draft = this.imDefaultTemplateAssetMatchingDraft;
    if (!draft?.assetClassId || !draft.subAssetClassId) {
      this.imDefaultTemplateNameOptions = [];
      this.showImDefaultTemplateCreateHelperInModal = false;
      this.hasResolvedImDefaultTemplateOptionsInModal = false;
      this.cr.markForCheck();
      return;
    }

    const partnerUid = this.getTemplateLookupPartnerUid();
    const scope = this.getDocumentTaggingScoresScope();
    if (!partnerUid || !scope?.scopeUid) {
      this.imDefaultTemplateNameOptions = [];
      this.showImDefaultTemplateCreateHelperInModal = false;
      this.hasResolvedImDefaultTemplateOptionsInModal = false;
      this.cr.markForCheck();
      return;
    }

    const payload = {
      partner_uid: partnerUid,
      scope_type: scope.scopeType,
      scope_uid: scope.scopeUid,
      is_standard: draft.type === 'standard' ? ('Y' as const) : ('N' as const),
      template_type: 'IM' as const,
      asset_class_ids: [Number(draft.assetClassId)],
      asset_subclass_ids: [Number(draft.subAssetClassId)],
    };

    this.templateDDService
      .getTemplatesByAssetDdl(payload)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const templateRows = this.getTemplateRowsFromAssetResponse(response);
          this.imDefaultTemplateNameOptions = templateRows
            .map((item: any) => {
              const label = String(
                item?.template_details?.template_name ??
                  item?.template_name ??
                  item?.name ??
                  item?.title ??
                  '',
              ).trim();
              const value = String(
                item?.templatedetails_id ??
                  item?.template_details?.templatedetails_id ??
                  item?.template_details?.id ??
                  item?.template_id ??
                  item?.id ??
                  '',
              ).trim();

              if (!label || !value) {
                return null;
              }

              return {
                label,
                value,
                mappingIds: Array.isArray(item?.mapping_ids)
                  ? item.mapping_ids.map((mappingId: unknown) => Number(mappingId)).filter((mappingId: number) => Number.isFinite(mappingId))
                  : [],
              } as DefaultTemplateAssetMatchingOption;
            })
            .filter((item): item is DefaultTemplateAssetMatchingOption => !!item);

          this.showImDefaultTemplateCreateHelperInModal = this.imDefaultTemplateNameOptions.length === 0;
          this.hasResolvedImDefaultTemplateOptionsInModal = true;

          const matchingDefaultRow = this.findMatchingDefaultTemplateRow(
            this.getExistingDefaultTemplateRowsFromAssetResponse(response),
            scope,
            {
              assetClassIds: [Number(draft.assetClassId)],
              assetSubclassIds: [Number(draft.subAssetClassId)],
            },
          );

          const currentTemplateId = String(
            matchingDefaultRow?.template_id ??
              draft.templateId ??
              this.pendingImDefaultTemplateSelection?.id ??
              this.imDefaultTemplateNameOptions[0]?.value ??
              '',
          ).trim();
          const selectedTemplate = this.imDefaultTemplateNameOptions.find((option) => option.value === currentTemplateId) ?? null;

          this.imDefaultTemplateAssetMatchingDraft = this.imDefaultTemplateAssetMatchingDraft
            ? {
                ...this.imDefaultTemplateAssetMatchingDraft,
                templateId: selectedTemplate?.value ?? '',
                templateName: selectedTemplate?.label ?? '',
                rowId: matchingDefaultRow ? Number(matchingDefaultRow.id ?? null) : null,
                sourceScopeType: this.normalizeDefaultTemplateScopeType(matchingDefaultRow?.scope_type),
              }
            : null;
          this.cr.markForCheck();
        },
        error: () => {
          this.imDefaultTemplateNameOptions = [];
          this.showImDefaultTemplateCreateHelperInModal = !this.hasImDefaultTemplateSelection();
          this.hasResolvedImDefaultTemplateOptionsInModal = true;
          this.cr.markForCheck();
        },
      });
  }

  private normalizeDefaultTemplateScopeType(value: unknown): 'PARTNER' | 'EMITTENT' | 'CLIENT' | null {
    const normalizedValue = String(value ?? '').trim().toUpperCase();
    return normalizedValue === 'PARTNER' || normalizedValue === 'EMITTENT' || normalizedValue === 'CLIENT'
      ? normalizedValue
      : null;
  }

  private formatTemplateApiDisplayValue(value: unknown): string {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
      return '';
    }

    return normalized
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private getTemplateRowsFromAssetResponse(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.data?.templates_details)) {
      return response.data.templates_details;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response?.results)) {
      return response.results;
    }
    return [];
  }

  private getExistingDefaultTemplateRowsFromAssetResponse(response: any): any[] {
    if (Array.isArray(response?.data?.default_templates)) {
      return response.data.default_templates;
    }
    if (Array.isArray(response?.default_templates)) {
      return response.default_templates;
    }
    return [];
  }

  private normalizeTemplateNumericArray(values: unknown): number[] {
    if (!Array.isArray(values)) {
      return [];
    }

    return values
      .map((value: unknown) => Number(value))
      .filter((value: number) => Number.isFinite(value))
      .sort((a: number, b: number) => a - b);
  }

  private hasMatchingDefaultTemplateCombination(
    rows: any[],
    scope: { scopeType: 'PARTNER' | 'EMITTENT' | 'CLIENT'; scopeUid: string },
    assetIds: { assetClassIds: number[]; assetSubclassIds: number[] },
  ): boolean {
    const expectedAssetClassIds = [...assetIds.assetClassIds].sort((a, b) => a - b);
    const expectedAssetSubclassIds = [...assetIds.assetSubclassIds].sort((a, b) => a - b);

    return rows.some((row) => {
      const rowScopeType = String(row?.scope_type ?? '').trim().toUpperCase();
      const rowScopeUid = String(row?.scope_uid ?? '').trim();
      const rowAssetClassIds = this.normalizeTemplateNumericArray(row?.asset_ids ?? row?.asset_class_ids);
      const rowAssetSubclassIds = this.normalizeTemplateNumericArray(row?.sub_asset_ids ?? row?.asset_subclass_ids);

      return (
        rowScopeType === scope.scopeType &&
        rowScopeUid === scope.scopeUid &&
        rowAssetClassIds.length === expectedAssetClassIds.length &&
        rowAssetClassIds.every((value, index) => value === expectedAssetClassIds[index]) &&
        expectedAssetSubclassIds.every((value) => rowAssetSubclassIds.includes(value))
      );
    });
  }

  private ensureDefaultTemplateEntryForSelection(
    templateType: 'DD' | 'IM',
    selectedTemplateType: DdTemplateKind,
    selectedTemplate: DdTemplateOption | null,
  ): Observable<unknown> {
    const selectedTemplateId = Number(this.normalizeTemplateDetailsId(selectedTemplate?.raw, selectedTemplate?.id));

    if (!this.productuid || !selectedTemplate || !Number.isFinite(selectedTemplateId) || !selectedTemplateType) {
      return of(null);
    }

    return this.resolveDdTemplateAssetIds().pipe(
      switchMap((assetIds) => {
        if (!assetIds) {
          return of(null);
        }

        const partnerUid = this.getTemplateLookupPartnerUid();
        const scope = this.getDocumentTaggingScoresScope();
        const modifiedByUid = this.getCurrentUserId();
        const mappingIds = this.getSelectedTemplateMappingIds(selectedTemplate, selectedTemplateType);

        if (!partnerUid || !scope?.scopeUid || !modifiedByUid || mappingIds.length === 0) {
          return of(null);
        }

        const lookupPayload = {
          partner_uid: partnerUid,
          scope_type: scope.scopeType,
          scope_uid: scope.scopeUid,
          is_standard: selectedTemplateType === 'standard' ? ('Y' as const) : ('N' as const),
          template_type: templateType,
          asset_class_ids: assetIds.assetClassIds,
          asset_subclass_ids: assetIds.assetSubclassIds,
        };

        return this.templateDDService.getTemplatesByAssetDdl(lookupPayload).pipe(
          take(1),
          switchMap((response) => {
            const existingRows = this.getExistingDefaultTemplateRowsFromAssetResponse(response);
            if (this.hasMatchingDefaultTemplateCombination(existingRows, scope, assetIds)) {
              return of(null);
            }

            return this.templateDDService
              .updateDefaultTemplate({
                id: null,
                source_scope_type: null,
                ...lookupPayload,
                template_id: selectedTemplateId,
                mapping_ids: mappingIds,
                modified_by_uid: modifiedByUid,
              })
              .pipe(
                take(1),
                catchError((error) => {
                  console.error('Failed to create default template entry from dataroom launch:', error);
                  return of(null);
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        console.error('Failed to verify default template entry before DD launch:', error);
        return of(null);
      }),
    );
  }

  private loadDdSignOffStatus() {
    if (!this.productuid || !this.canShowDueDiligence) {
      return;
    }

    const payload = {
      product_uid: this.productuid,
      template_type: 'DD',
      created_by_uid: this.getCurrentUserId(),
      is_analyst: this.isAnalystUser === true ? 'Y' : 'N',
    };

    this.dueDiligenceService
      .getProcessqueueDataByProductId(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          const details = Array.isArray(data?.details) ? [...data.details] : [];
          this.hasInProgressDdRun =
            String(data?.is_ai_running ?? '').toUpperCase() === 'Y' ||
            details.some((item: any) => String(item?.is_ai_running ?? '').toUpperCase() === 'Y');
          const latest = details.sort(
            (a: any, b: any) => new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime(),
          )[0];

          const approvedDate = latest?.analyst_approved_date;
          const analystStatus = String(latest?.analyst_status ?? '').toLowerCase();

          this.ddSignedOff = !!approvedDate || analystStatus.includes('approved') || analystStatus.includes('sign');
          this.cr.detectChanges();
        },
        error: () => {
          this.ddSignedOff = false;
          this.hasInProgressDdRun = false;
          this.cr.detectChanges();
        },
      });
  }

  private loadImInProgressStatus() {
    if (!this.productuid || !this.canShowDueDiligence) {
      return;
    }

    const payload = {
      product_uid: this.productuid,
      template_type: 'IM',
      created_by_uid: this.getCurrentUserId(),
      is_analyst: this.isAnalystUser === true ? 'Y' : 'N',
    };

    this.dueDiligenceService
      .getProcessqueueDataByProductId(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          const details = Array.isArray(data?.details) ? data.details : [];
          this.hasInProgressImRun =
            String(data?.is_ai_running ?? '').toUpperCase() === 'Y' ||
            details.some((item: any) => String(item?.is_ai_running ?? '').toUpperCase() === 'Y');
          this.cr.detectChanges();
        },
        error: () => {
          this.hasInProgressImRun = false;
          this.cr.detectChanges();
        },
      });
  }

  getCategoryNamesForMissingDoc() {
    // Only call if we have both partneruid and productuid
    if (!this.partneruid || !this.productuid) {
      return;
    }

    this.productService
      .getCategoryNamesForMissingDoc(this.partneruid, this.productuid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.categoryNamesForMissingDoc = res.data || [];
        },
        error: (err) => {
          console.error('Error fetching category names:', err);
        },
      });
  }

  insertTemplateProductMapping() {
    if (!this.productuid) {
      return;
    }

    const payload = {
      product_uid: this.productuid,
      emittent_uid: this.emittentuid,
      partner_uid: this.getCurrentPartnerUid(),
      templatedetails_id: this.getSelectedTemplateDetailsId(),
    };

    this.productService.insertTemplateProductMapping(payload).subscribe({
      next: (data) => {
        if (data?.status === 'FAILED') this.disableUpload = true;
      },
      error: (err) => {
        console.error('checkVectorDb API Error ❌', err);
        this.disableUpload = true;
      },
    });
  }

  // private createTemplateProductAssetMapping(): void {
  //   const partnerUid = this.getCurrentPartnerUid();
  //   const createdByUid = this.getCurrentUserId();
  //   const scope = this.getTemplateProductMappingScope();
  //   const assetCodename = this.normalizeTemplateProductAssetKey(this.productForm?.assetClass);
  //   const assetSubclassCodename = this.normalizeTemplateProductAssetKey(this.productForm?.assetSubclass);

  //   if (!this.productuid || !partnerUid || !createdByUid || !scope || !assetCodename || !assetSubclassCodename) {
  //     return;
  //   }

  //   this.templateDDService
  //     .getAssetClasses({ listName: 'Assetclass' })
  //     .pipe(
  //       take(1),
  //       map((rows) => this.findTemplateProductAssetClassId(rows, assetCodename)),
  //       switchMap((assetClassId) => {
  //         if (!assetClassId) {
  //           return of(null);
  //         }

  //         return this.templateDDService
  //           .getSubAssetClasses({
  //             listName: 'Subassetclass',
  //             Assetclassid: [assetClassId],
  //           })
  //           .pipe(
  //             take(1),
  //             map((rows) => {
  //               const subAssetClassId = this.findTemplateProductSubAssetClassId(
  //                 rows,
  //                 assetClassId,
  //                 assetSubclassCodename,
  //               );

  //               return subAssetClassId
  //                 ? {
  //                     partner_uid: partnerUid,
  //                     product_uid: this.productuid,
  //                     scope_type: scope.scopeType,
  //                     scope_uid: scope.scopeUid,
  //                     asset_class_ids: [assetClassId],
  //                     asset_subclass_ids: [subAssetClassId],
  //                     created_by_uid: createdByUid,
  //                   }
  //                 : null;
  //             }),
  //           );
  //       }),
  //       switchMap((payload) => (payload ? this.productService.createTemplateProductMapping(payload) : of(null))),
  //       catchError(() => of(null)),
  //     )
  //     .subscribe();
  // }

  getDocumentTaggingScores(shouldCall: boolean) {
    // Only call if we have both partneruid and productuid
    if (!this.partneruid || !this.productuid) {
      return;
    }

    const scope = this.getDocumentTaggingScoresScope();

    this.productService
      .getDocumentTaggingScores(
        this.productuid,
        this.partneruid,
        this.isAnalystUser === true ? 'Y' : 'N',
        scope?.scopeType ?? null,
        scope?.scopeUid ?? null,
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.totalScores = res?.overall_score_level;
          this.isDocDelete = res?.is_doc_delete;
          this.stopProgress();
          this.totalScoreslevel = res?.overall_confidence_score;
          this.vectorDbId = res?.vectordb_id;
          this.documents = res?.low_score_data_category;
          this.documentList = res?.documents;
          this.recommended_category_list = res?.recommended_category_list;
          this.setDocumentTaggingTemplateScope(res);
          this.setDefaultTemplateSelectionFromScores(res?.default_template);
          this.loadImDefaultTemplateSelection();
          this.vectorStoreJobStatus = res?.ai_status?.toLowerCase() === 'succeeded' || res?.ai_status?.toLowerCase() === 'failed';
          this.documentCategoryJobStatus = res?.category_ai_status?.toLowerCase() === 'succeeded' || res?.category_ai_status?.toLowerCase() === 'failed';
          //if(this.vectorStoreJobStatus) this.updateDocumentUploadFailureState(res?.documents);
          this.vectorStoreJobId = res?.job_id;
          if (shouldCall) this.startJobPolling(this.vectorStoreJobId);
          const scoreJob = this.getScoreCategoryJob(res, 'Score');
          const categoryJob = this.getScoreCategoryJob(res, 'Category');
          const scoreJobId = this.getBackgroundJobId(scoreJob);
          const categoryJobId = this.getBackgroundJobId(categoryJob) ?? res?.category_job_id ?? null;

          this.isScoringInProgress = this.isBackgroundJobInProgress(scoreJob);
          if (categoryJob) {
            this.productService.documentCategorizationSubject.next(this.isBackgroundJobInProgress(categoryJob));
          }

          if (shouldCall && this.isScoringInProgress) this.startScoringJobPolling(scoreJobId);
          this.documentCategoryJobId = categoryJobId;
          if (shouldCall && this.isBackgroundJobInProgress(categoryJob ?? { job_id: categoryJobId, status: res?.category_ai_status })) {
            this.startDocumentCategorizationJobPolling(this.documentCategoryJobId);
          }
          this.dataSource.setCategoryNamesForMandatoryDoc(res?.documents);
          this.dataSource.load(this.currentSort, this.activeScope?.value.id, this.documentList, this.vectorStoreJobStatus, this.documentCategoryJobStatus);
          if (this.productuid && !this.vectorDbId) this.checkVectorDb();
          this.cr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching document tagging scores:', err);
        },
      });
  }

  private refreshDueDiligencePartnerData(): void {
    this.getCategoryNamesForMissingDoc();
    this.validateDdTemplateScoreBeforeLoadingScores(true);

    if (!this.partneruid) {
      return;
    }

    this.productService
      .getActiveCategories(this.partneruid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.categoryNamesForMandatoryDoc = res || [];
          this.cr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching active categories:', err);
        },
      });
  }

  private validateDdTemplateScoreBeforeLoadingScores(shouldCall: boolean, partnerUid = this.getCurrentPartnerUid()): void {
    const scope = this.getDocumentTaggingScoresScope();

    if (!this.productuid || !partnerUid || !scope?.scopeUid) {
      this.getDocumentTaggingScores(shouldCall);
      return;
    }

    this.productService
      .validateDdTemplateScore({
        product_uid: this.productuid,
        partner_uid: partnerUid,
        scope_type: scope.scopeType,
        scope_uid: scope.scopeUid,
      })
      .pipe(
        untilDestroyed(this),
        finalize(() => this.getDocumentTaggingScores(shouldCall)),
      )
      .subscribe({
        error: (error) => console.error('Error validating default template score:', error),
      });
  }

  public canCheckScore() {
    return this.productuid ?? false;
  }

  public isAllCategorysUoloaded(): boolean {
    const currentNodes = (this.dataSource as any).nodes.getValue();
    const pdfNodes = currentNodes.filter(
      (node) => node.type === 'file' && node.file?.contentType === 'application/pdf',
    );
    return pdfNodes.length > 0 && pdfNodes.every((node) => node.category);
  }

  checkVectorDb(): void {
    // Only call checkVectorDb if we have a valid product_id
    if (!this.productuid) {
      console.warn('checkVectorDb: product_id is missing, skipping API call');
      return;
    }

    const payload = {
      product_id: this.productuid,
      vector_store_id: this.vectorDbId,
    };
    this.productService.checkVectorDb(payload).subscribe({
      next: (data) => {
        this.vectorDbId = data.vector_store_id;
        this.cr.detectChanges();
      },
      error: (err) => {
        console.error('checkVectorDb API Error ❌', err);
      },
    });
  }

  uploadVectorStore(emittentId: string | null, docId?: string[], callback?: () => void): void {
    this.productService.uploadVectorStoreSubject.next(true);
    const payload = {
      vs_id: this.vectorDbId,
      emittent_id: emittentId,
      product_id: this.productuid,
      doc_id: docId.join(','),
      extra_attributes: {
        additionalProp1: {},
      },
      poll: true,
    };
    this.productService.uploadVectorStoreV2(payload).subscribe({
      next: (data) => {
        //   this.vectorDbId = data?.vector_store_result?.vs_id || data?.vectordb_id ||
        // this.vectorDbId;
        this.vectorStoreJobId = data?.job_id;
        this.startJobPolling(this.vectorStoreJobId);
        if (callback) callback();
      },
      error: (err) => {
        console.error('Categorization API Error ❌', err);
        // ✅ ensure UI unlocks even on error
        this.productService.uploadVectorStoreSubject.next(false);
      },
    });
  }

  startDocumentCategorizationJobPolling(documentCategoryJobId: string | null) {
    if (documentCategoryJobId) {
      this.productService.documentCategorizationSubject.next(true);
      this.pollJobStatus(documentCategoryJobId).subscribe({
        next: (jobDetails) => {
          if (jobDetails?.status === 'succeeded') {
            this.productService.documentCategorizationSubject.next(false);
            this.getDocumentTaggingScores(false);
            this.getCategoryNamesForMissingDoc();
          } else if (jobDetails?.status === 'failed') {
            this.productService.documentCategorizationSubject.next(false);
            this.getDocumentTaggingScores(false);
            this.getCategoryNamesForMissingDoc();
          }
        },
        error: (err) => {
          this.productService.documentCategorizationSubject.next(false);
          console.error('Background polling failed', err);
        },
      });
    }
  }

  startJobPolling(vectorStoreJobId: string | null) {
    if (vectorStoreJobId) {
      this.productService.uploadVectorStoreSubject.next(true);
      this.pollJobStatus(vectorStoreJobId).subscribe({
        next: (jobDetails) => {
          if (jobDetails?.status === 'succeeded') {
            this.productService.uploadVectorStoreSubject.next(false);
            this.getDocumentTaggingScores(false);
          } else if (jobDetails?.status === 'failed') {
            this.productService.uploadVectorStoreSubject.next(false);
            this.getDocumentTaggingScores(false);
          }
        },
        error: (err) => {
          this.productService.uploadVectorStoreSubject.next(false);
          console.error('Background polling failed', err);
        },
      });
    }
  }

  private startScoringJobPolling(jobId: string | null, showAnalysisLoader = false): void {
    if (!jobId) {
      return;
    }

    this.isScoringInProgress = true;
    if (showAnalysisLoader) {
      this.isDefaultTemplateScoreRecalculationInProgress = true;
    }
    let pollingFinished = false;
    let pollingResponseCount = 0;

    this.pollJobStatus(jobId)
      .pipe(
        take(this.scoringJobPollingMaxAttempts),
        finalize(() => {
          if (pollingFinished) {
            return;
          }

          this.isScoringInProgress = false;
          if (showAnalysisLoader) {
            this.isDefaultTemplateScoreRecalculationInProgress = false;
          }
          if (pollingResponseCount >= this.scoringJobPollingMaxAttempts) {
            this.snackBarService.open(
              'Document analysis is taking longer than expected. Please refresh and try again later.',
              'warn',
            );
          }
        }),
      )
      .subscribe({
        next: (jobDetails) => {
          pollingResponseCount += 1;
          if (jobDetails?.status === 'succeeded' || jobDetails?.status === 'failed') {
            pollingFinished = true;
            this.isScoringInProgress = false;
            if (showAnalysisLoader) {
              this.isDefaultTemplateScoreRecalculationInProgress = false;
            }
            this.getDocumentTaggingScores(false);
          }
        },
        error: (err) => {
          pollingFinished = true;
          this.isScoringInProgress = false;
          if (showAnalysisLoader) {
            this.isDefaultTemplateScoreRecalculationInProgress = false;
          }
          console.error('Scoring polling failed', err);
        },
      });
  }

  private getScoreCategoryJob(response: any, jobType: 'Score' | 'Category'): any | null {
    const jobs = response?.score_category_job ?? response?.data?.score_category_job;
    const job = jobs?.[jobType] ?? jobs?.[jobType.toLowerCase()] ?? null;

    return Array.isArray(job) ? job.find((item) => this.getBackgroundJobId(item)) ?? null : job;
  }

  private getBackgroundJobId(job: any): string | null {
    const jobId = String(job?.job_id ?? '').trim();
    return jobId || null;
  }

  private isBackgroundJobInProgress(job: any): boolean {
    const jobId = this.getBackgroundJobId(job);
    const status = String(job?.status ?? '').trim().toLowerCase();

    return Boolean(jobId && status !== 'succeeded' && status !== 'failed');
  }

  private setDocumentTaggingTemplateScope(response: any): void {
    const scopeType = String(response?.template_scope_type ?? response?.data?.template_scope_type ?? '')
      .trim()
      .toUpperCase();
    const scopeUid = String(response?.template_scope_uid ?? response?.data?.template_scope_uid ?? '').trim();

    this.documentTaggingTemplateScope =
      scopeUid && (scopeType === 'PARTNER' || scopeType === 'EMITTENT' || scopeType === 'CLIENT')
        ? { scopeType, scopeUid }
        : null;
  }

  private startCurrentProductTemplateScoringJobPolling(response: any): void {
    const currentProductUid = String(this.productuid ?? '').trim();
    if (!currentProductUid) {
      return;
    }

    const impactedProducts = Array.isArray(response?.impacted_products_for_scoring)
      ? response.impacted_products_for_scoring
      : [];
    const isCurrentProductImpacted = impactedProducts.some(
      (product: any) => String(product?.product_uid ?? '').trim() === currentProductUid,
    );

    if (!isCurrentProductImpacted) {
      return;
    }

    const scoringJob = (Array.isArray(response?.scoring_jobs) ? response.scoring_jobs : []).find(
      (job: any) => String(job?.product_uid ?? '').trim() === currentProductUid,
    );
    const jobId = String(scoringJob?.job_id ?? '').trim();

    if (jobId) {
      this.startDefaultTemplateScoringJobPolling(jobId);
    }
  }

  private startDefaultTemplateScoringJobPolling(jobId: string): void {
    this.startScoringJobPolling(jobId, true);
  }

  // private updateDocumentUploadFailureState(documents: any[] = []): void {
  //   const failedCategories = [...new Set(
  //     (documents || [])
  //       .filter((document) => document?.is_doc_vectordb_created === 'N')
  //       .map((document) => document?.category_name)
  //       .filter((categoryName) => !!categoryName),
  //   )];

  //   this.documentUploadFailureMessage = failedCategories.length
  //     ? `Some documents were not uploaded successfully for analysis: ${failedCategories.join(', ')}.`
  //     : null;

  //   this.syncToolbarFailureMessage();
  // }

  // private syncToolbarFailureMessage(): void {
  //   this.failedMesssage = this.jobFailureMessage || this.documentUploadFailureMessage;
  //   this.showFailedMessage = !!this.failedMesssage;
  //   this.cr.markForCheck();
  // }

  startProgress() {
    this.progressValue = 0;
    this.showProgressBar = true;
    this.progressInterval = setInterval(() => {
      if (this.progressValue < 95) {
        this.progressValue += 5;
        this.cr.detectChanges();
      } else {
        clearInterval(this.progressInterval);
      }
    }, 2000);
  }

  stopProgress() {
    clearInterval(this.progressInterval);
    this.progressValue = 100;
    this.cr.detectChanges();

    setTimeout(() => {
      this.showProgressBar = false;
      this.cr.detectChanges();
    }, 1000);
  }

  public onStatusIndicatorClick(element: DataroomNode): void {
    if (element?.status !== 'failed') {
      return;
    }

    const emittendId = this.getEmittentId(element?.file?.url);

    const failedDocumentIds = this.getFailedDocumentIds();
    if (!failedDocumentIds.length || !emittendId) {
      return;
    }

    const dialogRef = this.dialog.open<DialogComponent, DialogData, DialogResult>(DialogComponent, {
      width: '560px',
      maxWidth: '92vw',
      disableClose: true,
      panelClass: 'reupload-failed-dialog-container',
      data: {
        title: 'Reupload Failed Document',
        titleIcon: {
          name: 'warning_amber',
          type: 'danger',
        },
        text: 'Are you sure you want to reprocess this document?',
        confirmButton: {
          label: 'OK',
          color: 'primary',
          type: 'raised',
          icon: 'refresh',
        },
        dismissButton: {
          label: 'Cancel',
          type: 'stroked',
        },
        shouldSeparateFooter: true,
        shouldStretchFooterButtons: true,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.vectorStoreJobStatus = false;
        this.refreshList();
        this.uploadVectorStore(emittendId, failedDocumentIds);
      }
    });
  }

  private getFailedDocumentIds(): string[] {
    return (this.documentList || [])
      .filter((document: any) => document?.is_doc_vectordb_created === 'N' && !!document?.document_id)
      .map((document: any) => String(document.document_id));
  }

  documentCategorization(emittentId: string | null, docId?: string[]): void {
    if (!this.partneruid || !this.productuid || !docId) {
      return;
    }

    const templateScope = this.documentTaggingTemplateScope ?? this.getDocumentTaggingScoresScope();
    if (!templateScope) {
      console.error('Unable to categorize documents: no document tagging scope is available.');
      this.snackBarService.open('Unable to categorize documents because no document tagging scope is available.', 'warn');
      return;
    }

    this.productService.documentCategorizationSubject.next(true);

    this.startProgress();
    const payload = {
      emittent_id: emittentId,
      partner_id: this.getCurrentPartnerUid(),
      product_id: this.productuid,
      node_id: docId.join(','),
      scope_type: templateScope.scopeType,
      scope_uid: templateScope.scopeUid,
      is_analyst: this.isAnalystUser === true ? 'Y' : 'N',
    };

    this.productService.documentCategorization(payload).subscribe({
      next: (res) => {
        // The background API returns independent jobs for categorization and scoring.
        this.documentCategoryJobId = String(res?.categorization_job_id ?? '').trim() || null;
        const scoringJobId = String(res?.scoring_jobs?.[0]?.job_id ?? '').trim() || null;

        this.startDocumentCategorizationJobPolling(this.documentCategoryJobId);
        this.startScoringJobPolling(scoringJobId);
        this.categorizationSubject.next(res);
        this.cr.detectChanges();
      },
      error: (err) => {
        console.error('Categorization API Error ❌', err);
        this.productService.documentCategorizationSubject.next(false);
      },
    });
  }

  public isCagageryEnabled(node_id_list: string[], emittentsID: string | null) {
    // Only call if we have required IDs
    if (!this.partneruid || !this.productuid || !node_id_list?.[0]) {
      return;
    }

    this.startProgress();
    const payload = {
      emittent_id: emittentsID,
      partner_id: this.getCurrentPartnerUid(),
      product_id: this.productuid,
      node_id: node_id_list[0],
    };

    this.productService.triggerCategorization(payload).subscribe({
      next: (res) => {
        this.categorizationSubject.next(res);
        this.getDocumentTaggingScores(false);
        this.getCategoryNamesForMissingDoc();
        this.cr.detectChanges();
      },
      error: (err) => {
        console.error('Categorization API Error ❌', err);
      },
    });
  }

  findIfAnyIsMandatory(categoryNamesForMandatoryDoc) {
    return categoryNamesForMandatoryDoc.some((category) => category.is_mandatory === 'Y');
  }

  private updateDocumentCategories(categorization: any): void {
    const currentNodes = (this.dataSource as any).nodes.getValue();
    const updatedNodes = currentNodes.map((node) => {
      let docUpdate = null;
      if (Array.isArray(categorization?.data)) {
        docUpdate = categorization.data.find((doc) => doc?.document_id === node?.id);
      } else if (categorization?.data?.document_id === node?.id) {
        docUpdate = categorization.data;
      }
      return docUpdate
        ? {
            ...node,
            isCalculating: docUpdate.document_category ? true : false,
            category: docUpdate.document_category,
            aiScore: docUpdate.score ? docUpdate.score : 0,
          }
        : node;
    });
    (this.dataSource as any).nodes.next(updatedNodes);
  }

  public canShowTable(): boolean {
    if (this.canShowDueDiligence && this.canCheckScore()) {
      return this.isDataHasFile();
    }
    return true;
  }

  public ngAfterViewInit(): void {
    this.shouldDisplayNoAccessPanel()
      .pipe(
        filter((should) => !should),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.bindDataChange();
      });
  }

  public getIcon(type: MediaType): IconDefinition {
    return getIcon(type);
  }

  public getIconColor(type: MediaType): string {
    return getIconColor(type);
  }

  public handleDoubleClick(node: DataroomNode): void {
    if (node.type === 'file') {
      this.download(node);
    } else {
      this.navigateTo(node);
    }
  }

  getScoreLabel(score: number): string {
    if (score < 40) return 'Low';
    if (score < 70) return 'Medium';
    return 'High';
  }

  getScoreShortLabel(score: number): string {
    if (score < 40) return 'L';
    if (score < 70) return 'M';
    return 'H';
  }

  public download(node: DataroomNode): void {
    this.actionsService.download(node).subscribe();
  }

  public bulkDownload(): void {
    this.actionsService.bulkDownload(this.selection.selected);
  }

  public preview(node: DataroomNode): void {
    if (!this.hasPreview(node)) return;
    this.actionsService.preview(node);
  }

  public getViewTooltip(node: DataroomNode): string {
    return this.hasPreview(node) ? '' : 'Document Not Supported';
  }

  public fileHasValue(node: DataroomNode) {
    return !!node.file;
  }

  public isBulkDownloadEnabled() {
    return (
      this.dataSource?.data?.length > 0 &&
      this.selection.selected.length > 0 &&
      this.selection.selected.every(this.fileHasValue)
    );
  }

  openDueDiligenceModal() {
    const dialogRef = this.dialog.open(this.dueDiligenceModal, {
      width: '550px',
      disableClose: true,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        // your logic or navigation here
      } else {
      }
    });
  }

  // public goToDueDiligence() {
  //  this.isProcessing = true;
  //  this.proceedToDueDiligence();
  //}

  public startDueDiligenceFlow() {
    if (this.isAdvisorUser) {
      return;
    }

    if (this.isLaunchUpgradeBlocked()) {
      this.openSubscriptionUpgradeDialog();
      return;
    }
    if (this.categoryNamesForMissingDoc?.length > 0) {
      this.openDueDiligenceModal();
      return;
    }

    this.launchDueDiligenceWithSavedFundName();
  }

  private getSelectedFundName(): string {
    return (this.selectedFundName || '').trim();
  }

  private getSavedSelectedFundName(): string {
    return (this.savedSelectedFundName || '').trim();
  }

  public getDisplayedSelectedFundName(): string {
    return this.getSavedSelectedFundName();
  }

  private getProductFundName(): string {
    return (this.productForm?.productName || this.productForm?.fund_name || '').trim();
  }

  private getFundNameStorageKey(): string {
    return `dd-selected-fund-name:${this.productuid || ''}`;
  }

  private loadSavedFundNameFromStorage(): void {
    const storedFundName = (localStorage.getItem(this.getFundNameStorageKey()) || '').trim();
    if (storedFundName) {
      this.savedSelectedFundName = storedFundName;
      this.selectedFundName = storedFundName;
    }
  }

  private persistSelectedFundName(fundName: string): void {
    const normalizedFundName = (fundName || '').trim();
    this.savedSelectedFundName = normalizedFundName;

    if (!normalizedFundName) {
      localStorage.removeItem(this.getFundNameStorageKey());
      return;
    }

    localStorage.setItem(this.getFundNameStorageKey(), normalizedFundName);
  }

  private normalizeFundNameResponse(response: any): string[] {
    const rawSuggestions =
      response?.fund_names ??
      response?.data?.fund_names ??
      response?.data?.names ??
      response?.names ??
      response?.data ??
      response;

    if (!Array.isArray(rawSuggestions)) {
      return [];
    }

    return rawSuggestions
      .map((item: any) =>
        typeof item === 'string'
          ? item
          : item?.selected_fund_name || item?.fund_name || item?.name || item?.title || item?.text || '',
      )
      .map((name: string) => name.trim())
      .filter(Boolean);
  }

  private getSelectedFundNameFromResponse(response: any): string {
    return (
      response?.selected_fund_name ??
      response?.data?.selected_fund_name ??
      response?.fund_name ??
      response?.data?.fund_name ??
      ''
    ).trim();
  }

  private setFundNameOptions(aiNames: string[], userProvidedNames: string[] = []): void {
    const normalizedUserProvidedNames = [
      ...new Set(userProvidedNames.map((name) => (name || '').trim()).filter(Boolean)),
    ];
    const mergedNames = [...new Set([...normalizedUserProvidedNames, ...aiNames].filter(Boolean))].slice(0, 6);

    this.fundNameOptions = mergedNames.map((name, index) => ({
      id: `fund-name-${index + 1}`,
      name,
      source: normalizedUserProvidedNames.includes(name) ? 'user' : 'ai',
      isEditing: false,
    }));

    if (!this.fundNameOptions.some((option) => option.name === this.selectedFundName)) {
      this.selectedFundName = this.fundNameOptions[0]?.name || '';
    }

    this.syncFundNameDialogState();
  }

  getFundNameSourceTooltip(option: FundNameOption): string {
    return option.source === 'ai' ? 'AI-generated' : 'User-provided';
  }

  private fetchFundNameOptions(showLoader = true) {
    if (!this.vectorDbId || !this.productuid) {
      if (showLoader) {
        this.isFundNameSuggestionsLoading = false;
        this.cr.detectChanges();
        this.syncFundNameDialogState();
      }

      return of({
        selectedName: '',
        names: [],
      });
    }

    if (showLoader) {
      this.isFundNameSuggestionsLoading = true;
      this.cr.detectChanges();
      this.syncFundNameDialogState();
    }

    const payload = {
      vs_id: this.vectorDbId,
      product_id: this.productuid,
    };

    return this.dueDiligenceService.getFundName(payload).pipe(
      map((response) => {
        const selectedName = this.getSelectedFundNameFromResponse(response);
        const names = this.normalizeFundNameResponse(response);
        return {
          selectedName,
          names,
        };
      }),
      catchError((error) => {
        console.error('Error loading fund names:', error);
        return of({
          selectedName: '',
          names: [],
        });
      }),
      finalize(() => {
        if (showLoader) {
          this.isFundNameSuggestionsLoading = false;
          this.cr.detectChanges();
          this.syncFundNameDialogState();
        }
      }),
    );
  }

  private applyFundNameOptions(selectedName: string, names: string[]): void {
    const currentSelectedFundName = this.getSelectedFundName();
    const persistedSelectedFundName = this.getSavedSelectedFundName();
    const fallbackProductFundName = this.getProductFundName();
    const normalizedSelectedName = (selectedName || '').trim();
    const userProvidedNames = [
      currentSelectedFundName,
      persistedSelectedFundName,
      fallbackProductFundName,
      normalizedSelectedName,
    ];

    if (normalizedSelectedName) {
      this.persistSelectedFundName(normalizedSelectedName);
    }

    this.selectedFundName =
      currentSelectedFundName || persistedSelectedFundName || normalizedSelectedName || fallbackProductFundName || '';
    this.setFundNameOptions(names, userProvidedNames);
    this.cr.detectChanges();
    this.syncFundNameDialogState();
  }

  private loadFundNames(): void {
    if (this.fundNameOptions.length === 0) {
      this.applyFundNameOptions('', []);
    }

    this.fetchFundNameOptions(true).subscribe(({ selectedName, names }) => {
      this.applyFundNameOptions(selectedName, names);
    });
  }

  private launchDueDiligenceWithSavedFundName(): void {
    const savedFundName = this.getSavedSelectedFundName();
    if (savedFundName) {
      this.selectedFundName = savedFundName;
      this.isProcessing = true;
      this.proceedToDueDiligence();
      return;
    }

    // Only call `/get-fund-name` when the user opens the modal (first time).
    this.openFundNameModal(true);
  }

  openFundNameModal(shouldReload = true): void {
    if (shouldReload && !this.hasFundNameLookupAttempted) {
      // Only hit `/get-fund-name` when we have the required ids, and only once per page load.
      if (this.productuid && this.vectorDbId) {
        this.hasFundNameLookupAttempted = true;
        this.loadFundNames();
      }
    }

    this.fundNameDialogRef = this.dialog.open(FundNameDialogComponent, {
      width: '440px',
      disableClose: true,
      panelClass: 'fund-name-dialog-container',
      data: {
        options: this.fundNameOptions,
        selectedFundName: this.selectedFundName,
        isLoading: this.isFundNameSuggestionsLoading,
      },
    });

    this.syncFundNameDialogState();

    this.fundNameDialogRef.afterClosed().subscribe((result: FundNameDialogResult | null) => {
      this.fundNameDialogRef = null;

      if (!result) {
        return;
      }

      this.selectedFundName = result.selectedFundName;
      this.fundNameOptions = result.options;
      this.confirmFundNameSelection();
    });
  }

  selectFundName(option: FundNameOption): void {
    this.selectedFundName = option.name;
  }

  enableFundNameEdit(option: FundNameOption, event?: Event): void {
    event?.stopPropagation();
    this.fundNameOptions.forEach((item) => {
      item.isEditing = item.id === option.id;
    });
    this.selectedFundName = option.name;
  }

  saveFundNameEdit(option: FundNameOption): void {
    option.name = (option.name || '').trim();
    option.source = 'user';
    option.isEditing = false;
    this.selectedFundName = option.name;
    this.cr.detectChanges();
  }

  handleFundNameAction(option: FundNameOption, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (option.isEditing) {
      this.saveFundNameEdit(option);
      return;
    }

    this.enableFundNameEdit(option, event);
  }

  cancelFundNameModal(): void {
    this.dialog.closeAll();
  }

  continueFromMissingDocuments(): void {
    this.dialog.closeAll();
    this.launchDueDiligenceWithSavedFundName();
  }

  confirmFundNameSelection(): void {
    const selectedFundName = this.getSelectedFundName();
    if (!selectedFundName) {
      return;
    }

    this.dialog.closeAll();
    this.isProcessing = true;

    const payload = {
      product_uid: this.productuid,
      selected_fund_name: selectedFundName,
      user_uid: this.getCurrentUserId(),
    };

    this.dueDiligenceService.updateProductSelectedFundNameByUser(payload).subscribe({
      next: () => {
        this.persistSelectedFundName(selectedFundName);
        this.proceedToDueDiligence();
      },
      error: (error) => {
        console.error('Error updating selected fund name:', error);
        this.isProcessing = false;
        this.snackBarService.open('Failed to save selected fund name.', 'error');
      },
    });
  }

  public goToDueDiligence() {
    if (this.isLaunchUpgradeBlocked()) {
      this.openSubscriptionUpgradeDialog();
      return;
    }
    this.isProcessing = true;
    this.proceedToDueDiligence();
  }

  public isLaunchBlockedByDdqRunLimitOnly(): boolean {
    return (
      this.isDdqRunsExhausted &&
      !this.isUploadingVectorStore &&
      !this.isDocumentCategorize &&
      !this.isProcessing &&
      !this.hasInProgressDdRun &&
      !this.hasInProgressImRun
    );
  }

  public isLaunchUpgradeBlocked(): boolean {
    return this.isLaunchBlockedByDdqRunLimitOnly();
  }

  /**
   * One tooltip key for the DD rocket, prioritized by launch state.
   */
  get dataroomDdRocketTooltipKey(): string {
    if (this.isAdvisorUser) {
      return 'INTL_LAUNCH_DD_NOT_AVAILABLE_FOR_ADVISORS';
    }
    if (this.isLaunchBlockedByDdqRunLimitOnly()) {
      return 'INTL_TOOLTIP_DDQ_RUN_LIMIT_REACHED_UPGRADE';
    }
    if (this.isUploadingVectorStore || this.isProcessing || this.isDocumentCategorize) {
      return 'INTL_TOOLTIP_DD_ROCKET_DOCUMENTS_ANALYZING';
    }
    if (this.hasInProgressDdRun || this.hasInProgressImRun) {
      return 'INTL_TOOLTIP_DD_ROCKET_PROCESS_RUNNING';
    }
    return 'INTL_TOOLTIP_RELAUNCH_DUE_DILIGENCE';
  }

  private openSubscriptionUpgradeDialog(): void {
    this.dialog.open(SubscriptionUpgradeDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        organizationType: 'lp',
      },
    });
  }

  private openCustomTemplateUpgradeDialog(): void {
    const organizationType = this.isGpCustomTemplateLocked && !this.isLpCustomTemplateLocked ? 'gp' : 'lp';
    this.dialog.open(SubscriptionUpgradeDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      disableClose: true,
      data: { organizationType },
    });
  }

  proceedToDueDiligence() {
    const currentUserId = this.getCurrentUserId();
    const payload = {
      template_type: 'DD',
      product_id: this.productuid,
      created_by_uid: currentUserId,
      is_analyst: this.isAnalystUser === true ? 'Y' : 'N',
      templatedetails_id: this.getSelectedTemplateDetailsId(),
      userType: this.getCurrentUserType(),
      org_name: this.getProcessQueueOrgName(),
      partner_uid: this.readActiveThemePartnerUid(),
      emittent_id: this.emittentId,
    };

    this.dueDiligenceService.insertProcessQueueData(payload).subscribe({
      next: (response) => {
        this.refreshSubscriptionAfterDdqLaunch();
        const payload = {
          processqueue_id: String(response.processqueue_id),
          product_id: this.productuid,
          created_by_uid: currentUserId,
          fund_name: this.getSelectedFundName(),
          firm_name: this.productForm.emittentCompany[0]?.name,
          templatedetails_id: this.getSelectedTemplateDetailsId(),
        };
        this.dueDiligenceService.setBehaviorSubjectValue(true);
        this.callGetRunQuestions(payload, response, null);
      },
      error: (error) => {
        console.error('Error inserting process queue data:', error);
        this.isProcessing = false;
      },
    });
  }

  private refreshSubscriptionAfterDdqLaunch(): void {
    this.subscriptionService
      .getCurrentSubscription(true)
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  private callGetRunQuestions(payload: any, response: any, option: any) {
    this.dueDiligenceService.getDDQRunQuestionsBackground(payload).subscribe({
      next: (runQueResponse: any) => {
        this.isProcessing = false;
        // Navigate only if this DDQ launch callback still belongs to the active dataroom flow.
        if (this.router.url.includes('/data-room') && !this.router.url.includes('/investment-memorandum-details/')) {
          const id = this.activatedRoute.snapshot.params['productId'];
          this.router.navigate(['/due-diligence-listing', id, response.processqueue_id], {
            queryParams: {
              action: option,
              emittentId: this.emittentId,
            },
            state: {
              returnUrl: this.router.url.replace('/data-room', '/due-diligence'),
            },
          });
        }

        // 2. Start Background Polling via Service
        this.pollJobStatus(runQueResponse.job_id).subscribe({
          next: (jobDetails) => {
            if (jobDetails?.status === 'succeeded' || jobDetails?.status === 'failed') {
              this.dueDiligenceService.setBehaviorSubjectValue(false);
            }
            if (jobDetails?.status === 'failed') {
              this.snackBarService.open('Internal Server Error', 'error');
              this.dueDiligenceService.setBehaviorSubjectValue(false);
            }
          },
          error: (err) => console.error('Background polling failed', err),
        });
      },
      error: (error) => {
        this.dueDiligenceService.setBehaviorSubjectValue(false);
        console.error('Initial call failed:', error);
      },
    });
  }

  public pollJobStatus(job_id: string) {
    return timer(0, 10000).pipe(
      // Start immediately, then every 5s
      untilDestroyed(this),
      switchMap(() => this.dueDiligenceService.getAiJobDetails({ job_id })),
      map((response: any) => response.find((item) => item.job_id === job_id)),
      takeWhile((job) => job?.status === 'queued' || job?.status === 'running', true), // Stop when completed
    );
  }

  public updateFile(file: File, node: DataroomNode): void {
    this.actionsService
      .updateFile(file, node, this.activeScope?.value.id)
      .pipe(tap(() => this.refreshList()))
      .subscribe();
  }

  public openRenameDialog(node: DataroomNode): void {
    this.actionsService
      .rename(node)
      .pipe(
        tap(() => {
          this.refreshList();
          this.service.resetAvailableNodes();
        }),
      )
      .subscribe();
  }

  public openDeleteConfirmationDialog(node: DataroomNode): void {
    this.actionsService
      .delete(node)
      .pipe(
        tap(() => {
          this.refreshList();
          if (this.productuid) this.deleteVectorStore(node);
        }),
      )
      .subscribe();
  }

  deleteVectorStore(node: DataroomNode) {
    const payload = {
      vs_id: this.vectorDbId,
      doc_id: node.id,
      file_id: '',
      delete_underlying_file: false,
      page_limit: 100,
    };
    this.productService.deleteVectorStore(payload).subscribe({
      next: () => {
        this.snackBarService.open('Document deleted successfully', 'success');
        this.updateDocumentCategoryMappingScore(node.id);
      },
      error: (err) => {
        console.error('Categorization API Error ❌', err);
      },
    });
  }

  private updateDocumentCategoryMappingScore(nodeId: string): void {
    const payload = { node_id: nodeId };
    this.productService
      .updateDocumentCategoryMappingScore(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.getDocumentTaggingScores(false);
        },
        error: (err) => {
          console.error('Error updating document category mapping score:', err);
        },
      });
  }

  public openUpdateDialog(node: DataroomNode): void {
    this.actionsService.uploadDialog(
      this.activeScope,
      () => {
        this.refreshList();
      },
      true,
      node,
    );
  }

  public upload(): void {
    const doucumentIdList: string[] = [];
    var emittentId : any = null;
    let isAlreadyCalled: boolean = false;

    if (this.shouldDisableToolbarActions) {
      this.snackBarService.error('INTL_READ_ONLY_USER_CANNOT_PERFORM_ACTION');
      return;
    }

    if (this.isTemplateSelectionRequiredForUpload()) {
      return;
    }

    if (this.hasUploadBlockingAiTask()) {
      return;
    }

    this.actionsService.uploadDialog(this.activeScope, (response?: any) => {
      this.refreshList();
      if (this.productuid) {
        if (response?.id) {
          this.totalScores = '';
          if (response?.file?.contentType === 'application/pdf') {
            doucumentIdList.push(response.id);
            emittentId = this.getEmittentId(response.file.url);
            //this.isCagageryEnabled([response.id], emittentId);
          }
        } else {
          if (doucumentIdList.length > 0 && !isAlreadyCalled) {
            isAlreadyCalled = true;
            this.documentCategorization(emittentId, doucumentIdList);
            this.uploadVectorStore(emittentId, doucumentIdList);
          }
        }
      }
    });
  }

  private getEmittentId(fileUrl?: string): string | null {
    if (!fileUrl) return null;

    const match = fileUrl.match(/emittents\/([^\/]+)/);
    return match?.[1] || null;
  }

  public recalculateScore(element: DataroomNode): void {
    const emittentId = this.getEmittentId(element.file?.url);
    //this.isCagageryEnabled([element.id], emittentId);
    this.documentCategoryJobStatus = false;
    this.refreshList(element?.id);
    this.documentCategorization(emittentId,[element.id]);
  }

  public openCreateFolderDialog(): void {
    if (this.shouldDisableToolbarActions) {
      this.snackBarService.error('INTL_NO_PRIVILEGES');
      return;
    }
    this.actionsService
      .createFolder(this.activeScope?.value.id)
      .pipe(tap(() => this.refreshList()))
      .subscribe();
  }

  public openMoveDialog(node: DataroomNode): void {
    if (this.shouldDisableToolbarActions) {
      this.snackBarService.error('INTL_NO_PRIVILEGES');
      return;
    }
    this.actionsService
      .move(node)
      .pipe(
        tap(() => {
          this.refreshList();
          this.service.resetAvailableNodes();
        }),
      )
      .subscribe();
  }

  public toggleSelect(row: DataroomNode): void {
    this.selection.toggle(row);
  }

  public toggleMaster(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.filter((row) => this.isFile(row)).forEach((row) => this.selection.select(row));
  }

  isDataHasFile() {
    return this.dataSource?.data.some((ele) => ele.type === 'file');
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.filter((row) => this.isFile(row)).length;
    return numSelected === numRows;
  }

  public markSelected(node: NavigableNode): void {
    if (node.id !== this.selectedScope.value.id) {
      this.selectedScope.next(node);
    } else {
      this.selectedScope.next(this.activeScope.value);
    }
  }

  public isRowSelected(row: DataroomNode): boolean {
    const selectedScope = this.selectedScope.value;
    return selectedScope?.id === row.id;
  }

  public isFile(node: DataroomNode): boolean {
    return node.type === 'file';
  }

  public hasPreview(node: DataroomNode): boolean {
    const type: any = node.file?.contentType;

    return type
      ? checkType(type, PdfMediaTypes) ||
          checkType(type, ImageMediaTypes) ||
          checkType(type, VideoMediaTypes) ||
          checkType(type, AudioMediaTypes)
      : false;
  }

  public get isAccessCheckRequired(): boolean {
    return (this.activatedRoute.snapshot.data as ConfigurableViewRouteData).accessCheckRequired;
  }

  public shouldDisplayNoAccessPanel(): Observable<boolean> {
    return combineLatest([
      this.accessGrant.asObservable(),
      this.organizationContextService.getEffectiveOrganizationType(),
      this.activatedRoute.data,
    ]).pipe(
      filterNil,
      filter(([_, organizationType, __]) => !!organizationType),
      map(([grant, organizationType, activatedRouteData]: [IssuingSectionStatusesDTO, string | null, Data]) => {
        if (this.organizationContextService.isInFundDDQRunsContextSync()) {
          return false;
        }

        if (!this.isAccessCheckRequired) {
          return false;
        }

        if (grant?.dataRoom && grant?.dataRoom !== 'unavailable') {
          return false;
        }

        return (
          this.authorizationService.isEmittentCompany(organizationType as OrganizationType) &&
          activatedRouteData?.issuing?.sectionStatuses?.dataRoom === 'unavailable'
        );
      }),
    );
  }

  public isDefaultReport(node: DataroomNode): boolean {
    return node.name === 'Prospectus' || node.name === 'Basis doc';
  }

  private bindDataChange(): void {
    const sortChange$ = this.sort?.sortChange
      ? (this.sort.sortChange as Observable<Sort>).pipe(startWith(defaultDataSourceOptions.sort))
      : of(defaultDataSourceOptions.sort);

    combineLatest([sortChange$, this.search.valueChanges.pipe(startWith(''))])
      .pipe(
        untilDestroyed(this),
        tap(([sort, phrase]) => {
          this.dataSource?.setOptions({ sort, search: phrase as string });
        }),
      )
      .subscribe();
  }

  private bindNavigationChange(): void {
    this.navigation
      .pipe(
        untilDestroyed(this),
        // startWith(routerState.snapshot) and the initial NavigationEnd resolve to the same
        // folder id on load, which fired the 'nodes' request twice. Collapse identical
        // folder ids so only genuine navigations trigger a reload.
        map((event) => this.getFolderId((event as NavigationEnd).url)),
        distinctUntilChanged(),
        switchMap((id) => {
          // Breadcrumb clicks may drop query params; re-apply DDQ context from resolved route
          // summary so data-room requests keep using emittent/issuer URL namespace.
          this.organizationContextService.primeFundDdqContextFromActivatedRoute(this.activatedRoute);
          this.ensureFundDdqContext();
          if (id === dataroomRoute) {
            this.loadInitialList();
            return of(this.mainNode);
          }
          return this.selectNodeDetails(id);
        }),
        tap((entity) => {
          if (entity) {
            this.goTo(this.toNavigableNode(entity));
          }
        }),
      )
      .subscribe();
  }

  private bindPathUpdates(): void {
    this.activeScope
      .asObservable()
      .pipe(
        untilDestroyed(this),
        switchMap((active) => this.selectNodeDetails(active.id)),
        map((node) => {
          const path: Breadcrumb[] = [];
          const isUserDataroom = this.isUserDataroom();
          if (node.id) {
            node.ancestors.forEach((ancestor) =>
              path.push({
                label: ancestor.name,
                routerLink: this.buildBreadcrumbNodeLink(ancestor.id, isUserDataroom),
              }),
            );
            path.push({
              label: node.name,
              routerLink: this.buildBreadcrumbNodeLink(node.id, isUserDataroom),
            });
          }
          return path;
        }),
        tap((chunks) => {
          this.breadcrumbs.updateItems([this.mainNodeRoute, ...chunks]);
        }),
      )
      .subscribe();
  }

  private goTo(node: NavigableNode): void {
    this.activeScope.next(node);
    this.selectedScope.next(node);
    this.dataSource.load(this.currentSort, node.id, this.documentList, this.vectorStoreJobStatus, this.documentCategoryJobStatus);
  }

  private navigateTo(node: DataroomNode): void {
    if (node?.type === 'file') {
      return;
    }
    this.router.navigate(['./', node.id], {
      relativeTo: this.activatedRoute,
      queryParams: this.activatedRoute.snapshot.queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private loadInitialList(): void {
    this.dataSource.load(this.currentSort, mainParentId, this.documentList, this.vectorStoreJobStatus, this.documentCategoryJobStatus);
  }

  private refreshList(documentId? : string): void {
    this.dataSource.load(this.currentSort, this.activeScope?.value.id, this.documentList, this.vectorStoreJobStatus, this.documentCategoryJobStatus, documentId);
  }

  private selectNodeDetails(id: string): Observable<DataroomNode> {
    return this.query
      .select((state) => state.nodes)
      .pipe(
        untilDestroyed(this),
        map((nodes) => {
          if (id === mainParentId) {
            return DataroomNode.create(new DataroomNode());
          }
          return nodes && nodes.find((n) => n.id === id);
        }),
        switchMap((found) => {
          if (id === mainParentId) {
            return of(this.mainNode);
          } else if (!found?.id) {
            return this.service.getNode(id).pipe(take(1));
          } else {
            return of(found);
          }
        }),
        distinctUntilChanged((a, b) => a.id === b.id),
        shareReplay(),
      );
  }

  private toNavigableNode(node: DataroomNode): NavigableNode {
    return { id: node.id, name: node.name };
  }

  private getFolderId(url: string): string {
    // NavigationEnd.url may contain query params (e.g. `data-room?context=...`).
    // We must strip them before extracting the last path segment, otherwise it becomes part of the node id.
    const pathOnly = url.split('?', 1)[0] ?? url;
    const segments = pathOnly.split('/', pathOnly.length);
    return segments[segments.length - 1];
  }

  private get currentSort(): Sort {
    return {
      direction: this.sort?.direction,
      active: this.sort?.active,
    };
  }

  private get emptyNode(): NavigableNode {
    return {
      id: mainParentId,
      name: null,
    };
  }

  private get mainNode(): DataroomNode {
    return DataroomNode.create({
      name: this.translate.instant('INTL_DATAROOM'),
      id: '',
      ancestors: [],
    });
  }

  private get mainNodeRoute(): Breadcrumb {
    return {
      label: this.translate.instant('INTL_DATAROOM'),
      routerLink: this.buildBreadcrumbMainLink(this.isUserDataroom()),
    };
  }

  private buildBreadcrumbMainLink(isUserDataroom: boolean): UrlTree {
    const queryParams = this.activatedRoute.snapshot.queryParams || {};
    if (isUserDataroom) {
      return this.router.createUrlTree(['/data-room'], { queryParams });
    }

    const productId =
      this.activatedRoute.snapshot.paramMap.get('productId') ||
      this.activatedRoute.parent?.snapshot.paramMap.get('productId');

    if (productId) {
      return this.router.createUrlTree(['/product', productId, 'data-room'], { queryParams });
    }

    return this.router.createUrlTree(['./'], {
      relativeTo: this.activatedRoute,
      queryParams,
    });
  }

  private buildBreadcrumbNodeLink(nodeId: string, isUserDataroom: boolean): UrlTree {
    const queryParams = this.activatedRoute.snapshot.queryParams || {};
    if (isUserDataroom) {
      return this.router.createUrlTree(['/data-room', nodeId], { queryParams });
    }

    const productId =
      this.activatedRoute.snapshot.paramMap.get('productId') ||
      this.activatedRoute.parent?.snapshot.paramMap.get('productId');

    if (productId) {
      return this.router.createUrlTree(['/product', productId, 'data-room', nodeId], { queryParams });
    }

    return this.router.createUrlTree(['./', nodeId], {
      relativeTo: this.activatedRoute,
      queryParams,
    });
  }

  private isUserDataroom(): boolean {
    return this.router.url?.startsWith('/data-room') ?? false;
  }

  showIncludeExcludeSummary(node: DataroomNode) {
    //const currentUrl = window.location.href;
    if (this.productuid && node.type === 'file' && !!node.file) {
      return true;
    } else {
      return false;
    }
  }

  showKeyMetricsButton(node: DataroomNode): boolean {
    // Only show for product dataroom files with actual files
    //const currentUrl = window.location.href;
    if (!this.productuid || node.type !== 'file' || !node.file) {
      return false;
    }

    // Show button for all eligible files - multiple files can be marked as key metrics
    return true;
  }

  openSummaryStatusChangeDialog(node: DataroomNode) {
    this.actionsService
      .changeSummaryStatus(node)
      .pipe(
        tap((data) => {
          if (data?.displayInProductSummary) {
            this.snackBarService.success('INTL_FILE_ADDED_SUCCESSFULLY');
          } else if (data?.displayInProductSummary === false) {
            this.snackBarService.success('INTL_FILE_REMOVED_SUCCESSFULLY');
          } else {
            this.snackBarService.error('INTL_DIALOG_CANCELED');
          }
          this.refreshList();
          this.service.resetAvailableNodes();
        }),
      )
      .subscribe();
  }

  openKeyMetricsStatusChangeDialog(node: DataroomNode) {
    this.actionsService
      .changeKeyMetricsStatus(node)
      .pipe(
        tap((data) => {
          if (data?.keyMetric) {
            this.snackBarService.success('INTL_FILE_MARKED_AS_KEY_METRICS');
            this.keyMetricsStatusChanged.emit(true);
          } else if (data?.keyMetric === false) {
            this.snackBarService.success('INTL_FILE_UNMARKED_AS_KEY_METRICS');
            this.keyMetricsStatusChanged.emit(false);
          } else {
            this.snackBarService.error('INTL_DIALOG_CANCELED');
          }
          this.refreshList();
          this.service.resetAvailableNodes();
        }),
      )
      .subscribe();
  }

  openRegulatoryDocumentStatusChangeDialog(node: DataroomNode) {
    this.actionsService
      .changeRegulatoryDocumentStatus(node)
      .pipe(
        tap((data) => {
          if (data?.regulatoryRequiredDocument) {
            this.snackBarService.success('INTL_FILE_MARKED_AS_REGULATORY');
          } else if (data?.regulatoryRequiredDocument === false) {
            this.snackBarService.success('INTL_FILE_UNMARKED_AS_REGULATORY');
          } else {
            this.snackBarService.error('INTL_DIALOG_CANCELED');
          }
          this.refreshList();
          this.service.resetAvailableNodes();
        }),
      )
      .subscribe();
  }

  showMenuButton(element: DataroomNode): boolean {
    return (
      this.showIncludeExcludeSummary(element) ||
      this.showUpdateButton(element) ||
      this.showDeleteButton(element) ||
      this.showRenameButton(element) ||
      this.showMoveButton(element)
    );
  }

  showUpdateButton(element: DataroomNode): boolean {
    return element.type === 'file' && !!element.file && !this.capabilities.isReadOnly && !element.readOnly;
  }

  showDeleteButton(element: DataroomNode) {
    return !element.readOnly;
  }
  public isDeleteDisabled(): boolean {
    return (
      this.isDocDelete === 'N' ||
      this.ddSignedOff ||
      this.isEmissionLocked ||
      this.organizationContextService.isInFundDDQRunsContextSync()
    );
  }
  showRenameButton(element: DataroomNode) {
    return !element.readOnly;
  }
  showMoveButton(element: DataroomNode) {
    return !element.readOnly && !this.shouldDisableToolbarActions;
  }

  showInfo(event: Event, element) {
    event.stopPropagation();
    this.dialog.open(NodeInfoDialogComponent, {
      width: '300px',
      data: element,
    });
  }

  public truncateFileName(fileName: string): string {
    return this.filenameTruncateService.truncateFileName(fileName, 45);
  }

  public getNodeDisplayName(node: DataroomNode): string {
    return node?.name || node?.file?.name || '';
  }

  /**
   * Open create fund dialog
   * Called when person_add icon is clicked beside the upload button
   * For investor organizations: defaults to invite_issuer flow and hides flow selection
   */
  public openCreateFundDialog(): void {
    const fundDDQRun = this.createFundDialogPrefillService.resolveForProduct(this.productuid);
    const dialogRef = this.dialog.open(CreateFundDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        fromProductCreationHeaderNav: true, // Hide flow selection and default to invite_issuer for investor orgs
        ...(fundDDQRun ? { fundDDQRun } : {}),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh the dataroom list after fund creation or invitation
        this.refreshList();
      }
    });
  }

  // Ai Score
  aiScore = 78; // example: dynamic value from API
  circumference = 2 * Math.PI * 20;

  private isEmissionStartedOrAwaiting(): boolean {
    const currentRouteEmission =
      this.activatedRoute.snapshot?.data?.['product']?.sectionStatuses?.emission ||
      this.activatedRoute.snapshot?.data?.['issuing']?.sectionStatuses?.emission;
    const parentRouteEmission = this.activatedRoute.parent?.snapshot?.data?.['product']?.sectionStatuses?.emission;

    const emission = String(currentRouteEmission || parentRouteEmission || '').toLowerCase();
    return emission === 'started' || emission === 'awaiting';
  }

  private resolveProductIdFromRouteTree(): string | null {
    let s: ActivatedRouteSnapshot | null = this.activatedRoute.snapshot;
    while (s) {
      const id = s.paramMap.get('productId');
      if (id) {
        return id;
      }
      s = s.parent ?? null;
    }
    return null;
  }

  private resolveEmittentIdFromRouteOrOrgContext(): string | null {
    let s: ActivatedRouteSnapshot | null = this.activatedRoute.snapshot;
    while (s) {
      const q = s.queryParamMap.get('issuerUid') || s.queryParamMap.get('issuerid');
      if (q) {
        return q;
      }
      s = s.parent ?? null;
    }
    if (this.organizationContextService.isInFundDDQRunsContextSync()) {
      return this.organizationContextService.getEffectiveOrganizationIdSync();
    }
    return null;
  }

  private ensureFundDdqContext(): void {
    this.organizationContextService.ensureFundDdqContextForClientDdqRoute(this.activatedRoute);
  }
}
